import React, { useState, useEffect } from "react";
import "./summarypage.css";
import { FaArrowLeft, FaSyncAlt, FaCalendarAlt } from "react-icons/fa";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faLocationDot, faSliders, faCircleExclamation, faBullseye } from "@fortawesome/free-solid-svg-icons";
import { useLocation, useNavigate, useSearchParams } from "react-router-dom";
import PriviewsGoalReview from "./priviewsgoalreview";
import { buildDomainTagsFromGoal, buildDomainTagsFromDataMap } from "./goalDomainTags";
import { newestVersionGoals } from "./newestGoalVersion";
import Toast from "./Toast";
import LoadingOverlay from "./LoadingOverlay";
import { API_BASE } from "../apiConfig";




// -- Status options + the helper copy shown under Status / Result Date --
const STATUS_OPTIONS = [
    "In progress - on track",
    "In progress - delayed",
    "Achieved",
    "Not achieved by target date",
];

const STATUS_DESCRIPTIONS = {
    "In progress - on track":
        "Progress is occurring as expected and the goal remains realistic and feasible. Review and refine the intervention, mode, and dose with any new prognostic information and/or goal-limiting factors.",
    "In progress - delayed":
        "Progress is occurring, but it is slower than expected or has extended beyond the original timeframe. Review and refine the intervention, mode, and dose with any new prognostic information and/or goal-limiting factors.",
    "Achieved":
        "The goal has been met and new goal setting is required. Review the intervention, mode, and dose, along with prognostic information and goal-limiting factors.",
    "Not achieved by target date":
        "Little or no progress has been made toward the goal by the target date. Re-evaluation and new goal setting are required. Review the intervention, mode, and dose, along with prognostic information and goal-limiting factors.",
};

// Legacy statuses saved in the DB under the old labels are mapped to the new
// wording so existing goals render and save under the updated option set.
const STATUS_MAP = {
    "In Progress- on track": "In progress - on track",
    "In progress- on track": "In progress - on track",
    "In Progress- delayed": "In progress - delayed",
    "In progress- delayed": "In progress - delayed",
    "Not Progressed as on date": "In progress - delayed",
    "Not Progressed as on Date": "In progress - delayed",
    "Achieved": "Achieved",
    "Not Achieved as on target date": "Not achieved by target date",
    "Not achieved as on target date": "Not achieved by target date",
};

const normalizeStatus = (s) => {
    const t = String(s || "").trim();
    return STATUS_MAP[t] || t;
};

// Statuses that END the goal journey. Both of the non-"In progress" options make
// a goal eligible for closure, so both open the "close this goal?" confirmation -
// this used to be hard-coded to "Achieved" only, which made "Not achieved by
// target date" unreachable: the old Goal Closed button refused it with a warning,
// and there was no other route to the popup at all.
const TERMINAL_STATUSES = ["Achieved", "Not achieved by target date"];

const isTerminalStatus = (s) => TERMINAL_STATUSES.includes(normalizeStatus(s));

// The goals that are actually eligible for closure right now: not yet closed, and
// carrying a terminal status. This is the single source of truth for "can this be
// closed", so the popup trigger (Update) and the popup's own Yes action can never
// disagree about which goals are involved.
const closableGoals = (goalList, edits) =>
    goalList.filter((g) => {
        if (Number(g.is_closed) === 1) return false;
        const edit = (edits || {})[g.goal_id] || {};
        return isTerminalStatus(edit.status || g.status || "");
    });

// True when at least one goal is closable, i.e. closing is now possible and the
// confirmation popup should be offered.
const hasClosableGoal = (goalList, edits) => closableGoals(goalList, edits).length > 0;

// "Newest edit wins" between the planning goals and the latest review snapshot,
// so this form, /updategoal, /goalsetdownload and the Review Existing Goals
// table always agree on which SMART goal / outcome measure / dosage is current.
//
// A Goal Review appends an immutable review snapshot (goal_type = 'review'),
// and so does the Review Smart Goal revision. Either one can be the most recent
// statement of the goal, so the planning rows are shown only when they were
// written AFTER the last snapshot. That rule used to be a local copy here; it
// now lives in newestGoalVersion.js so the pages that need it cannot drift
// apart.

// -- Small numeric stepper used for dosage / sessions counts --
const Counter = ({ label, value, onChange, min = 0, disabled = false }) => {
    const numericValue = Number(value) || 0;
    const [editing, setEditing] = useState(false);

    const handleIncrement = () => !disabled && onChange(numericValue + 1);
    const handleDecrement = () => !disabled && onChange(Math.max(min, numericValue - 1));
    const handleInputChange = (e) => {
        if (disabled) return;
        const raw = e.target.value;
        if (raw === "") {
            onChange(min);
            return;
        }
        const parsed = Number(raw);
        if (!Number.isNaN(parsed)) {
            onChange(Math.max(min, parsed));
        }
    };

    return (
        <div className="grv-counter-field">
            <label>{label}</label>
            <div className="grv-counter">
                <button type="button" onClick={handleDecrement} aria-label={`Decrease ${label}`} disabled={disabled}>
                    -
                </button>
                <input
                    type="number"
                    className="grv-counter-input"
                    aria-label={label}
                    value={editing ? (editing === "clear" ? "" : editing) : numericValue}
                    min={min}
                    onChange={(e) => {
                        setEditing(e.target.value);
                        handleInputChange(e);
                    }}
                    onFocus={() => {
                        if (disabled) return;
                        if (numericValue === 0) setEditing("clear");
                    }}
                    onBlur={() => {
                        setEditing(false);
                        if (numericValue === 0 && (value === 0 || value === "0" || value === "" || value == null)) {
                            onChange(0);
                        }
                    }}
                    disabled={disabled}
                    readOnly={disabled}
                />
                <button type="button" onClick={handleIncrement} aria-label={`Increase ${label}`} disabled={disabled}>
                    +
                </button>
            </div>
        </div>
    );
};

export default function GoalReviewpage() {
    const navigate = useNavigate();
    const location = useLocation();
    const token = localStorage.getItem("token");

    // -- History mode: Client History -> "Review" on a single row --
    // Opened as /goalreview-history?sessionId=..&reviewId=.. (the dedicated
    // history route), or as goalreview?sessionId=..&mode=history&reviewId=..
    // The page then shows ONE saved version with exactly the same layout as
    // the review page, minus every editing action. reviewId=0 is the
    // original Goal Set row, any other value is that review's version.
    const [searchParams] = useSearchParams();
    const isHistoryRoute = location.pathname === "/goalreview-history";
    const isHistoryView = isHistoryRoute || searchParams.get("mode") === "history";
    const historyReviewId = parseInt(searchParams.get("reviewId") || "0", 10) || 0;

    const [currentSessionId, setCurrentSessionId] = useState(null);
    const [interventionsData, setInterventionsData] = useState(null);
    const [loadingInterventions, setLoadingInterventions] = useState(false);
    const [isSaving, setIsSaving] = useState(false);
    const [showCloseConfirm, setShowCloseConfirm] = useState(false);

    // review_id written by the Update that opened the close popup. The
    // confirmation amends that review instead of appending a new one, so a
    // single Update + Confirm leaves ONE line in Client History rather than two
    // identical ones.
    const [lastReviewId, setLastReviewId] = useState(0);
    // -- Blocking spinner for the "Review Assessment" hand-off --
    // That button used to fire navigate() directly, so the frame between the click
    // and /start-assessment mounting was a blank white screen. The hand-off is not
    // instant either: it first awaits get_smart_goal_context.php to resolve the
    // session to resume, so there is a real, visible stall with nothing on it.
    // This is the same overlay the PDF buttons use, now shared via
    // LoadingOverlay instead of a fifth copy-pasted inline block.
    const [isStartingAssessment, setIsStartingAssessment] = useState(false);
    const [isEditing, setIsEditing] = useState(false);
    const [updateButtonText, setUpdateButtonText] = useState("Update");
    const [goalClosedDate, setGoalClosedDate] = useState(null);
    const [toast, setToast] = useState(null);
    // Review session resolved for this client (used by "Continue Pathway").
    // Editing review goals implies a Review flow, so continue should resume
    // the REVIEW questions even when goalreview was opened with an initial
    // sessionId.
    const [reviewSessionId, setReviewSessionId] = useState(null);
    // Age group + client_id of the resolved review session. Passed to
    // /review-assessment on "Continue Pathway" so the Review questions open
    // with the correct age-based pathway (the review age can differ from the
    // initial goalreview session's age_group).
    const [reviewSessionAge, setReviewSessionAge] = useState(null);
    const [reviewClientId, setReviewClientId] = useState(null);
    // "review" | "review smart goal" - the mode of the session Continue Pathway
    // resumes, so it reopens on the right page/mode.
    const [reviewApptType, setReviewApptType] = useState("");

    // -- Runs when user taps "Yes, Confirm" in the popup --
    // Only the goals that are actually eligible get closed. The old separate "Goal
    // Closed" button refused to run at all unless EVERY goal was terminal, so this
    // loop unconditionally closing `goals` was safe by construction. Routing the
    // same popup through Update removed that precondition - now it can be opened
    // while some goals are still "In progress" - so closing everything here would
    // shut goals that were never reviewed. `closableGoals` is the same list the
    // popup trigger tested, so what is asked and what is closed cannot drift.
    const confirmGoalClosed = async () => {
        setShowCloseConfirm(false);
        const targets = closableGoals(goals, goalEdits);
        if (targets.length === 0) {
            setToast({ message: "No goal is ready to be closed yet.", type: "warning" });
            return;
        }
        setIsSaving(true);
        try {
            // Stage every target's closure BEFORE the save, so a single POST
            // carries all of them. This used to loop one POST per target, and
            // because each POST re-read the other goals' is_closed from state
            // that had not been refetched, every iteration undid the previous
            // one's closure and only the last goal ended up closed.
            //
            // The ids are ALSO passed through opts.closeIds rather than left to
            // the goalEdits state above: updateGoalEdit schedules a state update,
            // so reading goalEdits in this same tick would still see the old
            // values and the save would carry no closure at all.
            const closeIds = new Set(targets.map((g) => g.goal_id));
            targets.forEach((goal) => updateGoalEdit(goal.goal_id, "is_closed", 1));

            // Amend the review the Update already wrote. Confirming the popup is
            // the second half of that ONE Update, not a second edit - appending
            // here wrote the same "Achieved" status to Client History twice.
            const { ok, reviewId } = await saveGoalReview(targets[0], {
                close: true,
                closeIds,
                reviewId: lastReviewId,
            });
            const allOk = ok;
            if (reviewId) setLastReviewId(reviewId);
            if (allOk) {
                const stillOpen = goals.length - targets.length;
                // Show the "Goal closed on {date}" footer only when the form is
                // actually finished. Setting it on a partial close would claim the
                // whole review is closed while goals remain open, and the footer
                // gate is `isFormLocked || goalClosedDate`.
                if (stillOpen === 0) {
                    const today = new Date();
                    const months = ["jan", "feb", "mar", "apr", "may", "jun", "jul", "aug", "sept", "oct", "nov", "dec"];
                    setGoalClosedDate(`${today.getDate()} ${months[today.getMonth()]} ${today.getFullYear()}`);
                }
                setIsEditing(false);
                setToast({
                    message:
                        stillOpen === 0
                            ? "Smart Goal Closed Successfully"
                            : `${targets.length} goal${targets.length > 1 ? "s" : ""} marked as closed. ${stillOpen} still open - set the status to Achieved or Not achieved by target date to close ${stillOpen > 1 ? "those" : "it"}.`,
                    type: "success",
                });
                fetchInterventions();
            } else {
                setToast({ message: "Some goals failed to close. Please try again.", type: "error" });
            }
        } finally {
            setIsSaving(false);
        }
    };

    // -- Runs when user taps "No, cancel" --
    // Declining is NOT undoing anything. The Update that opened this popup has
    // already been written to the server - the popup only ever asks the extra
    // question "now close the goal as well?". This says so out loud, because a
    // confirmation dialog sitting on top of a just-saved form reads like the save
    // is still pending and people re-click Update to be sure.
    const declineGoalClosed = () => {
        setShowCloseConfirm(false);
        setToast({
            message: "Goal review saved. The goal was not closed.",
            type: "info",
        });
    };

    // goalEdits: { [goal_id]: { status, resultDate, demonstratedWell, strengthened, actions, outcomeFollowUp } }
    const [goalEdits, setGoalEdits] = useState({});
    // interventionEdits: { "goalId-idx": { minutes, weeks, sessions } }
    const [interventionEdits, setInterventionEdits] = useState({});
    // Key components fetched from API per intervention excel_sheet
    const [keyComponentsMap, setKeyComponentsMap] = useState({});
    // Active Key Components tab per goal (goal_id -> family name from API).
    // Falls back to the FIRST tab when a goal has no explicit selection.
    const [activeKcTab, setActiveKcTab] = useState({});

    // -- True when goalreview was opened from the Goals page "Edit" button.
    // The "Continue Pathway" button only makes sense when arriving from the
    // Review flow (start-assessment ? Review), so it is hidden here. --
    const [fromGoalsPage, setFromGoalsPage] = useState(false);

    // -- Session ID Initialization --
    useEffect(() => {
        const urlParams = new URLSearchParams(location.search);
        const urlSessionId = urlParams.get("sessionId");

        if (urlParams.get("fromGoalsPage") === "1") {
            setFromGoalsPage(true);
        }

        if (urlSessionId) {
            setCurrentSessionId(urlSessionId);
            return;
        }

        const savedState = localStorage.getItem("assessmentState");
        if (savedState) {
            try {
                const parsed = JSON.parse(savedState);
                if (parsed.currentSessionId) setCurrentSessionId(parsed.currentSessionId);
            } catch (e) {
                console.error("Error parsing saved state", e);
            }
        }
    }, [location.search]);

    // -- Fetch Data --
    // json.goals        -> every goal for this session, rendered editable (top section)
    // json.review_goals -> goals that ALREADY have a saved review, rendered
    //                      read-only in a separate section below (className
    //                      "grv-page getall")
    const fetchInterventions = async () => {
        if (!currentSessionId) return;

        setInterventionsData(null);
        setLoadingInterventions(true);

        try {
            const response = await fetch(
                `${API_BASE}/therapist_api/get_test_intervention.php`,
                {
                    method: "POST",
                    headers: {
                        "Content-Type": "application/json",
                        Authorization: `Bearer ${token}`,
                    },
                    body: JSON.stringify({ session_id: parseInt(currentSessionId) }),
                }
            );

            const json = await response.json();

            if (json.status && json.data) {
                setInterventionsData(json);

                const goalsList = json.goals || [];
                const reviewGoalsList = json.review_goals || [];

                // History mode reads ONE specific version instead of the
                // latest: reviewId=0 shows the original planning goals
                // (Goal Set row), any other id shows that review's goals.
                const requestedReview =
                    reviewGoalsList.find(
                        (review) => Number(review.review_id) === historyReviewId
                    ) || null;

                // Editable goals = the NEWEST version (mirrors
                // get_goals_page.php): when the planning goals were written or
                // revised AFTER the last review snapshot (the "Revise Selected
                // Intervention" flow), the form is seeded from those planning
                // goals - with their current SMART goal, outcome measure and
                // dosage, and blank review-only fields. Otherwise the latest
                // review snapshot is shown, as before. History mode reads the
                // exact requested version.
                const editableGoals = isHistoryView
                    ? historyReviewId === 0
                        ? goalsList
                        : requestedReview?.goals || []
                    : newestVersionGoals(goalsList, reviewGoalsList);

                if (editableGoals.length > 0) {
                    const seededGoalEdits = {};
                    const seededInterventionEdits = {};

                    // -- Carry the last recorded review notes forward ----------
                    // A Review Smart Goal revision appends a Goal Review snapshot
                    // that carries the revised goal fields but NONE of the review
                    // notes: insert_pathway_goals.php writes neither
                    // outcome_measure_used nor the components_* columns. The moment
                    // that snapshot is the newest version, newestVersionGoals()
                    // returns it and the review-only fields below would come back
                    // EMPTY - the notes the therapist had already saved in the last
                    // Goal Review disappeared from the form after an RSG, which is
                    // what this fixes: whatever was recorded last stays on screen.
                    //
                    const getDraftGoalValue = (goal, field, goalIndex = 0) => {
                        try {
                            const raw = localStorage.getItem(`goalreview_draft_${currentSessionId}`);
                            if (!raw) return "";
                            const draft = JSON.parse(raw);
                            if (!draft || typeof draft !== "object") return "";
                            const order = Number(goal.goal_order) || (goalIndex + 1);
                            const goalDraft =
                                draft.byOrder?.[order] ||
                                draft.byGoalId?.[goal.goal_id] ||
                                draft[goal.goal_id] ||
                                (Object.keys(draft).length === 1 ? Object.values(draft)[0] : null);
                            if (!goalDraft) return "";
                            const val = goalDraft[field];
                            if (val !== null && val !== undefined && String(val).trim() !== "") {
                                if (field === "status") {
                                    const s = normalizeStatus(val);
                                    if (s && s.toLowerCase() !== "select") return s;
                                    return "";
                                }
                                return val;
                            }
                        } catch (e) {
                            console.error("Error reading goalreview draft from localStorage:", e);
                        }
                        return "";
                    };

                    const lastRecordedReviewValue = (goal, field, goalIndex = 0) => {
                        const order = Number(goal.goal_order) || 0;
                        // 1. Search backwards through review snapshots (newest to oldest)
                        for (let i = reviewGoalsList.length - 1; i >= 0; i--) {
                            const snapGoals = reviewGoalsList[i]?.goals || [];
                            const match =
                                (order > 0 ? snapGoals.find((g) => Number(g.goal_order) === order) : null) ||
                                snapGoals.find((g) => {
                                    const d1 = JSON.stringify(g.goal_domain || "");
                                    const d2 = JSON.stringify(goal.goal_domain || "");
                                    return d1 && d1 !== '""' && d1 === d2;
                                }) ||
                                snapGoals[goalIndex] ||
                                snapGoals[0];
                            if (!match) continue;
                            const value = match[field];
                            if (
                                value !== null &&
                                value !== undefined &&
                                String(value).trim() !== ""
                            ) {
                                if (field === "status") {
                                    const s = normalizeStatus(value);
                                    if (!s || s.toLowerCase() === "select") continue;
                                    return s;
                                }
                                return value;
                            }
                        }

                        // 2. Fallback: Search planning goals (goalsList)
                        for (let i = goalsList.length - 1; i >= 0; i--) {
                            const g = goalsList[i];
                            const gOrder = Number(g.goal_order) || 0;
                            const matchPlan =
                                (order > 0 && gOrder === order) ||
                                (goalsList.length === 1) ||
                                (i === goalIndex);
                            if (!matchPlan) continue;
                            const value = g[field];
                            if (
                                value !== null &&
                                value !== undefined &&
                                String(value).trim() !== ""
                            ) {
                                if (field === "status") {
                                    const s = normalizeStatus(value);
                                    if (!s || s.toLowerCase() === "select") continue;
                                    return s;
                                }
                                return value;
                            }
                        }

                        return "";
                    };

                    // -- HISTORY MODE: notes carried forward into a revision -----
                    // A Review Smart Goal revision appends a snapshot row
                    // (goal_type = 'review') that carries the revised goal fields
                    // but can come back WITHOUT the review notes: the therapist
                    // recorded them on the PREVIOUS review of the same goal, not
                    // on the revision itself. Reading only the clicked row's own
                    // values then showed blank fields on the newest history row
                    // even though the notes were saved.
                    //
                    // So when the clicked version has no notes of its own, the
                    // last notes ACTUALLY RECORDED for that same goal are shown -
                    // searched only through OLDER reviews, never newer ones, so
                    // opening an old history row can never be changed by a later
                    // update. Status and dates are deliberately excluded: they
                    // belong to the version being viewed, not to the journey.
                    const lastRecordedValueBeforeRequestedReview = (goal, field, goalIndex = 0) => {
                        if (!isHistoryView || historyReviewId <= 0) return "";
                        const reqIdx = reviewGoalsList.findIndex(
                            (review) => Number(review.review_id) === historyReviewId
                        );
                        if (reqIdx <= 0) return "";

                        const order = Number(goal.goal_order) || 0;
                        for (let i = reqIdx - 1; i >= 0; i--) {
                            const snapGoals = reviewGoalsList[i]?.goals || [];
                            const match =
                                (order > 0
                                    ? snapGoals.find((g) => Number(g.goal_order) === order)
                                    : null) ||
                                snapGoals.find((g) => {
                                    const d1 = JSON.stringify(g.goal_domain || "");
                                    const d2 = JSON.stringify(goal.goal_domain || "");
                                    return d1 && d1 !== '""' && d1 === d2;
                                }) ||
                                snapGoals[goalIndex] ||
                                snapGoals[0];
                            if (!match) continue;
                            const value = match[field];
                            if (
                                value !== null &&
                                value !== undefined &&
                                String(value).trim() !== ""
                            ) {
                                return value;
                            }
                        }
                        return "";
                    };

                    const reviewValueOrLast = (goal, ownValue, field, draftField, goalIndex = 0) => {
                        if (isHistoryView) {
                            if (ownValue !== null && ownValue !== undefined && String(ownValue).trim() !== "") {
                                return ownValue;
                            }
                            if (field === "status") return "";
                            return lastRecordedValueBeforeRequestedReview(goal, field, goalIndex);
                        }
                        if (ownValue !== null && ownValue !== undefined && String(ownValue).trim() !== "") {
                            if (field === "status") {
                                const s = normalizeStatus(ownValue);
                                if (s && s.toLowerCase() !== "select") return s;
                            } else {
                                return ownValue;
                            }
                        }
                        const lastRec = lastRecordedReviewValue(goal, field, goalIndex);
                        if (lastRec) return lastRec;
                        if (draftField) {
                            const draftVal = getDraftGoalValue(goal, draftField, goalIndex);
                            if (draftVal) return draftVal;
                        }
                        return "";
                    };

                    editableGoals.forEach((goal, gIdx) => {
                        const firstIntervention =
                            (goal.interventions || [])[0] || {};

                        seededGoalEdits[goal.goal_id] = {
                            /*
                             * HISTORY MODE must show the clicked version exactly
                             * as it was saved, so it never borrows from the
                             * newest review. The fallback below used to run here
                             * too, and because the Goal Set row (reviewId 0)
                             * carries the PLANNING goal - whose status is still
                             * the "Select" placeholder - it painted the LATEST
                             * review's status onto it. Clicking a history row
                             * therefore showed updated data. result_date had the
                             * same problem, defaulting to today.
                             */
                            status: isHistoryView
                                ? normalizeStatus(goal.status) || STATUS_OPTIONS[0]
                                : (() => {
                                      // A planning goal that was revised on /updategoal
                                      // keeps the "Select" placeholder, so fall back to
                                      // the latest review's real status - otherwise an
                                      // Update click would wipe the current status out of
                                      // the row (mirrors get_goals_page.php's effective
                                      // status).
                                      const s = normalizeStatus(goal.status);
                                      if (s && s !== "Select") return s;
                                      // Same carry-forward rule as the notes below:
                                      // the newest snapshot (an RSG revision) can
                                      // carry the "Select" placeholder, so read the
                                      // last status that was actually recorded
                                      // instead of dropping it back to the default.
                                      const ls = normalizeStatus(
                                          lastRecordedReviewValue(goal, "status", gIdx)
                                      );
                                      if (ls && ls !== "Select") return ls;
                                      const ds = normalizeStatus(
                                          getDraftGoalValue(goal, "status", gIdx)
                                      );
                                      if (ds && ds !== "Select") return ds;
                                      return STATUS_OPTIONS[0];
                                  })(),
                            is_closed: Number(goal.is_closed) === 1 ? 1 : 0,
                            // Show the date that was actually saved for this
                            // version; only fall back to today for a goal that
                            // has never been given one.
                            resultDate: goal.result_date || (isHistoryView ? "" : getTodayDateString()),
                            dose_of_intervention:
                                firstIntervention.dose_of_intervention ??
                                goal.dose_of_intervention ??
                                "",
                            demonstratedWell: reviewValueOrLast(
                                goal,
                                goal.components_demonstrated,
                                "components_demonstrated",
                                "demonstratedWell",
                                gIdx
                            ),
                            strengthened: reviewValueOrLast(
                                goal,
                                goal.components_strengthened,
                                "components_strengthened",
                                "strengthened",
                                gIdx
                            ),
                            actions: reviewValueOrLast(
                                goal,
                                goal.components_specific_action,
                                "components_specific_action",
                                "actions",
                                gIdx
                            ),
                            outcomeFollowUp: reviewValueOrLast(
                                goal,
                                goal.outcome_measure_used,
                                "outcome_measure_used",
                                "outcomeFollowUp",
                                gIdx
                            ),
                        };

                        (goal.interventions || []).forEach((iv, idx) => {
                            seededInterventionEdits[`${goal.goal_id}-${idx}`] = {
                                minutes: iv.dosage_minutes_per_day ?? 0,
                                weeks: iv.dosage_weeks ?? 0,
                                sessions: iv.therapist_sessions ?? 0,
                            };
                        });
                    });
                    setGoalEdits(seededGoalEdits);
                    setInterventionEdits(seededInterventionEdits);
                }
            }
        } catch (error) {
            console.error("Error fetching interventions:", error.message);
        } finally {
            setLoadingInterventions(false);
        }
    };

    useEffect(() => {
        if (currentSessionId) fetchInterventions();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [currentSessionId]);

    // -- Fetch Key Components for all selected interventions --
    useEffect(() => {
        const allGoals = interventionsData?.goals || [];
        const reviewGoalsList = interventionsData?.review_goals || [];

        // Must match the version rendered above: history mode fetches the key
        // components of the requested version, not the latest one.
        let goalsList;
        if (isHistoryView) {
            const requestedReview =
                reviewGoalsList.find(
                    (review) => Number(review.review_id) === historyReviewId
                ) || null;
            goalsList =
                historyReviewId === 0
                    ? allGoals
                    : requestedReview?.goals || [];
        } else {
            goalsList = newestVersionGoals(allGoals, reviewGoalsList);
        }

        if (goalsList.length === 0) return;

        const allSheets = new Set();
        goalsList.forEach((goal) => {
            getSelectedInterventions(goal).forEach((s) => allSheets.add(s));
        });

        if (allSheets.size === 0) return;

        const fetchKC = async () => {
            const newMap = {};
            for (const sheet of allSheets) {
                const item = findInterventionTile(sheet);
                if (!item || !item.id) continue;
                try {
                    const res = await fetch(
                        `${API_BASE}/therapist_api/get_key_components.php`,
                        {
                            method: "POST",
                            headers: {
                                "Content-Type": "application/json",
                                Authorization: `Bearer ${token}`,
                            },
                            body: JSON.stringify({
                                intervention_id: item.id,
                                session_id: parseInt(currentSessionId),
                            }),
                        }
                    );
                    const json = await res.json();
                    if (json.status && json.data?.key_components) {
                        newMap[sheet] = {
                            ...json.data.key_components,
                            family: json.data.family || sheet,
                        };
                    }
                } catch (err) {
                    console.error("Error fetching key components for", sheet, err.message);
                }
            }
            setKeyComponentsMap(newMap);
        };
        fetchKC();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [interventionsData]);

    // -- Resolve the client's REVIEW session id ----------------------------
    // goalreview already knows the client via interventionsData.session
    // (client_id / client_name / age_group). Find the matching review session
    // from get_review_pathway_details.php so "Continue Pathway" resumes the
    // Review questions (appointment_type = review) instead of the initial ones.
    const resolveReviewSessionId = async () => {
        if (!token || !interventionsData || !interventionsData.session) return null;
        const s = interventionsData.session;
        const norm = (v) => String(v == null ? "" : v).trim().toLowerCase();
        try {
            const res = await fetch(
                `${API_BASE}/therapist_api/get_review_pathway_details.php`,
                {
                    method: "POST",
                    headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
                }
            );
            const json = await res.json();
            if (!json.status || !Array.isArray(json.data)) return null;

            const clientObj = json.data.find(
                (c) =>
                    (s.client_id != null && String(c.client_id) === String(s.client_id)) ||
                    (s.client_name && norm(c.client_name) === norm(s.client_name))
            );
            if (!clientObj) return null;

            const sessions = Array.isArray(clientObj.sessions) ? clientObj.sessions : [];
            const getAge = (ss) => norm(ss.age_group || ss.age);
            const getSid = (ss) => (ss.sessionId != null ? ss.sessionId : ss.session_id != null ? ss.session_id : null);
            // "Continue Pathway" must resume a REVIEW-type session only.
            // Resuming an initial session would re-open the initial pathway with
            // its initial answers, so an initial session must never be picked.
            //
            // "Review Smart Goal" is a peer of "Review", not a variant of it, and
            // it gets its own pathway and its own session. It used to be excluded
            // here, so a client whose only review-style session was an RSG one
            // resolved to chosen = null and "Continue Pathway" started a brand
            // new plain Review instead of resuming their work.
            const isReview = (ss) => {
                const t = (ss.appointment_type || ss.type || "").toLowerCase();
                return t === "review" || t === "review smart goal";
            };
            const reviewSessions = sessions.filter(isReview);
            // Resolve the client's review session when one exists, but still
            // return the client_id even when there is none so "Continue
            // Pathway" can create the review session from Review-assessment.
            let chosen = null;
            if (reviewSessions.length > 0) {
                // Prefer a review session matching the goalreview session's age
                // group; otherwise fall back to the most recently created one.
                const ageMatched = s.age_group
                    ? reviewSessions.filter((ss) => getAge(ss) === norm(s.age_group))
                    : reviewSessions;
                const pool = ageMatched.length > 0 ? ageMatched : reviewSessions;
                chosen = pool.reduce((best, ss) => {
                    const sid = Number(getSid(ss));
                    return best === null || sid > Number(getSid(best)) ? ss : best;
                }, null);
            }
            return {
                sessionId: chosen ? getSid(chosen) : null,
                age_group: chosen ? (chosen.age_group || chosen.age || null) : null,
                // Which of the two review modes the resolved session belongs to.
                // "Continue Pathway" has to reopen the page in that exact mode,
                // otherwise a Review Smart Goal session reopens as a plain
                // Review and the whole revision context is lost.
                appointmentType: chosen ? String(chosen.appointment_type || chosen.type || "").toLowerCase() : "",
                client_id: clientObj.client_id || null,
            };
        } catch (error) {
            console.error("Resolve review session failed:", error.message);
            return null;
        }
    };

    useEffect(() => {
        if (!interventionsData || !interventionsData.session) return;
        let cancelled = false;
        resolveReviewSessionId().then((info) => {
            if (!cancelled && info) {
                setReviewSessionId(info.sessionId);
                setReviewSessionAge(info.age_group);
                setReviewClientId(info.client_id);
                setReviewApptType(info.appointmentType || "");
            }
        });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [interventionsData]);

    // input[type=date] requires YYYY-MM-DD
    const getTodayDateString = () => {
        const today = new Date();
        const yyyy = today.getFullYear();
        const mm = String(today.getMonth() + 1).padStart(2, "0");
        const dd = String(today.getDate()).padStart(2, "0");
        return `${yyyy}-${mm}-${dd}`;
    };

    const formatDateDisplay = (dateString) => {
        if (!dateString || dateString === "-" || dateString === "null" || dateString === "undefined") return "-";
        const raw = String(dateString).trim();
        const months = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
        const match = raw.match(/^(\d{4})-(\d{1,2})-(\d{1,2})/);
        if (match) {
            const y = match[1];
            const mIdx = parseInt(match[2], 10) - 1;
            const d = String(parseInt(match[3], 10)).padStart(2, "0");
            if (mIdx >= 0 && mIdx < 12) {
                return `${d} ${months[mIdx]} ${y}`;
            }
        }
        const match2 = raw.match(/^(\d{1,2})[/-](\d{1,2})[/-](\d{4})/);
        if (match2) {
            const d = String(parseInt(match2[1], 10)).padStart(2, "0");
            const mIdx = parseInt(match2[2], 10) - 1;
            const y = match2[3];
            if (mIdx >= 0 && mIdx < 12) {
                return `${d} ${months[mIdx]} ${y}`;
            }
        }
        const date = new Date(raw);
        if (isNaN(date.getTime())) return dateString;
        const d = String(date.getDate()).padStart(2, "0");
        const m = months[date.getMonth()];
        const y = date.getFullYear();
        return `${d} ${m} ${y}`;
    };

    const formatClosedDate = (dateString) => {
        if (!dateString) return "-";
        const date = new Date(dateString);
        if (isNaN(date.getTime())) return dateString;
        const months = ["jan", "feb", "mar", "apr", "may", "jun", "jul", "aug", "sept", "oct", "nov", "dec"];
        return `${date.getDate()} ${months[date.getMonth()]} ${date.getFullYear()}`;
    };

    // Some review goals store the skill merged into the domain as
    // "Domain-Skill" (e.g. "Play & Cognition-Play and Cognition" when the
    // domain has no sub-list). Normalise away the duplicate name so the chip
    // shows the domain once.
    const normalizeDomain = (s) =>
        String(s || "")
            .toLowerCase()
            .replace(/&/g, "and")
            .replace(/[^a-z0-9]/g, "");

    const cleanGoalDomain = (d) => {
        const parts = String(d || "")
            .split("-")
            .map((p) => p.trim())
            .filter(Boolean);
        if (parts.length > 1) {
            const firstNorm = normalizeDomain(parts[0]);
            if (firstNorm && parts.every((p) => normalizeDomain(p) === firstNorm)) {
                return parts[0];
            }
        }
        return d;
    };

    const updateGoalEdit = (goalId, field, value) => {
        setGoalEdits((prev) => {
            const next = {
                ...prev,
                [goalId]: { ...(prev[goalId] || {}), [field]: value },
            };
            try {
                if (currentSessionId) {
                    const existingDraftRaw = localStorage.getItem(`goalreview_draft_${currentSessionId}`);
                    const existingDraft = existingDraftRaw ? JSON.parse(existingDraftRaw) : {};
                    const byOrder = existingDraft.byOrder || {};
                    const byGoalId = existingDraft.byGoalId || {};

                    const goalList = interventionsData?.goals || [];
                    const reviewGoalList = interventionsData?.review_goals || [];
                    const effectiveList = newestVersionGoals(goalList, reviewGoalList);
                    const goalObj = effectiveList.find((g) => g.goal_id === goalId) || effectiveList[0];
                    const order = Number(goalObj?.goal_order) || 1;

                    byOrder[order] = { ...(byOrder[order] || {}), [field]: value };
                    byGoalId[goalId] = { ...(byGoalId[goalId] || {}), [field]: value };

                    localStorage.setItem(
                        `goalreview_draft_${currentSessionId}`,
                        JSON.stringify({
                            ...existingDraft,
                            ...next,
                            byOrder,
                            byGoalId,
                        })
                    );
                }
            } catch (e) {
                console.error("Failed to update goalreview draft in localStorage:", e);
            }
            return next;
        });
    };

    const updateInterventionEdit = (key, field, value) => {
        setInterventionEdits((prev) => ({
            ...prev,
            [key]: { ...prev[key], [field]: value },
        }));
    };

    // Finds the catalog entry for an intervention by its excel_sheet value
    // (the value stored in pathway_goal_interventions.selected_intervention).
    // Used to render the key-components tiles (image + H2 description).
    const findInterventionTile = (excelSheet) => {
        if (!interventionsData?.data) return null;
        const norm = (s) => String(s || "").toLowerCase().replace(/[\s:.-]+/g, "");
        const target = norm(excelSheet);
        if (!target) return null;
        for (const key of Object.keys(interventionsData.data)) {
            const item = (interventionsData.data[key] || []).find(
                (i) => i.excel_sheet && norm(i.excel_sheet) === target
            );
            if (item) return item;
        }
        return null;
    };

    // Unique list of interventions the caregiver selected for a goal (deduped
    // across the goal's intervention rows, order preserved).
    const getSelectedInterventions = (goal) => {
        const sheets = [];
        (goal.interventions || []).forEach((iv) => {
            const raw = Array.isArray(iv.selected_intervention)
                ? iv.selected_intervention
                : typeof iv.selected_intervention === "string" && iv.selected_intervention.trim()
                    ? iv.selected_intervention.split(",")
                    : [];
            raw.forEach((s) => {
                const t = String(s || "").trim();
                if (t && !sheets.includes(t)) sheets.push(t);
            });
        });
        return sheets;
    };

    // -- Save a single goal's review to the backend --
    // Every save posts the WHOLE goal set, not just the goal that was
    // edited. A save with no `opts.reviewId` becomes its own new review line
    // on the server, so (a) a multi-goal session never loses the other goals'
    // review data and (b) Client History gets one new line per update instead
    // of the same line being overwritten.
    //
    // `opts.reviewId` amends that review INSTEAD of appending another one. The
    // endpoint deletes the review's goal rows and re-inserts the posted set
    // under the same review_id. This matters because "Update" and the close
    // confirmation that follows it are ONE user action split across two steps:
    // saving the status and closing the goal are not two edits to the goal
    // journey, they are one edit the therapist is asked to confirm. Appending
    // both wrote the same status twice, so Client History showed "Achieved" on
    // two consecutive lines a couple of seconds apart.
    const saveGoalReview = async (goal, opts = {}) => {
        // No review_id is sent any more: the server appends a new review on
        // every save, which is what keeps the previous version visible.
        const interventionsPayloadFor = (g) => {
            const rows = (g.interventions || []).map((iv, idx) => {
                const e = interventionEdits[`${g.goal_id}-${idx}`] || {};
                return {
                    intervention_type: iv.intervention_type,
                    selected_intervention: iv.selected_intervention,
                    dosage_minutes_per_day: e.minutes,
                    dosage_weeks: e.weeks,
                    therapist_sessions: e.sessions,
                    dose_of_intervention:
                        (goalEdits[g.goal_id] || {}).dose_of_intervention,
                };
            });

            /* A goal with no intervention row still renders ONE blank dosage
               block (see `interventionBlocks`), so the therapist can record home
               practice and session counts for a goal whose caregiver named no
               intervention. Without this the counters would be editable and then
               silently discarded on save. Sent only once a number was actually
               entered, so untouched goals never gain an empty row. */
            if (!(g.interventions || []).length) {
                const e = interventionEdits[`${g.goal_id}-0`] || {};
                const minutes = Number(e.minutes) || 0;
                const weeks = Number(e.weeks) || 0;
                const sessions = Number(e.sessions) || 0;
                if (minutes || weeks || sessions) {
                    rows.push({
                        intervention_type: null,
                        selected_intervention: null,
                        dosage_minutes_per_day: minutes,
                        dosage_weeks: weeks,
                        therapist_sessions: sessions,
                        dose_of_intervention:
                            (goalEdits[g.goal_id] || {}).dose_of_intervention,
                    });
                }
            }
            return rows;
        };

        // Closing a goal must NOT overwrite the selected status (e.g.
        // "Achieved") with "Closed" - the closure is tracked separately via
        // is_closed, so the preview can still display the status the
        // therapist actually chose. The goal being saved applies its own
        // edits; the other goals carry their current values so a multi-goal
        // session always saves a complete review.
        //
        // A PENDING is_closed edit counts for every goal, not just the target.
        // It used to be read off `g.is_closed` alone, which is state from the
        // last refetch - so closing several goals at once (each one staged with
        // updateGoalEdit) could never be carried by a single save, because the
        // non-target goals ignored the edit that had just been staged for them.
        const review_goals = goals.map((g) => {
            const isTarget = goal && g.goal_id === goal.goal_id;
            const e = goalEdits[g.goal_id] || {};

            const status = normalizeStatus(e.status) || normalizeStatus(g.status) || STATUS_OPTIONS[0];

            const pendingClosed = e.is_closed === undefined ? null : Number(e.is_closed) === 1 ? 1 : 0;
            const forcedClosed = opts.closeIds && opts.closeIds.has(g.goal_id) ? 1 : null;
            const closed = isTarget && opts.close
                ? 1
                : forcedClosed !== null
                    ? forcedClosed
                    : pendingClosed !== null
                        ? pendingClosed
                        : Number(g.is_closed) === 1
                            ? 1
                            : 0;

            return {
                goal_domain: g.goal_domain,
                goal_skill: g.goal_skill,
                status,
                status_content: STATUS_DESCRIPTIONS[status] || STATUS_DESCRIPTIONS[STATUS_OPTIONS[0]],
                smart_goal: e.smartGoal || g.smart_goal,
                target_date: e.targetDate || g.target_date,
                result_date: e.resultDate || g.result_date,
                dose_of_intervention: e.dose_of_intervention || g.dose_of_intervention,
                is_closed: closed,
                outcome_measure: g.outcome_measure,
                outcome_measure_used: e.outcomeFollowUp,
                components_demonstrated: e.demonstratedWell,
                components_strengthened: e.strengthened,
                components_specific_action: e.actions,
                interventions: interventionsPayloadFor(g),
            };
        });

        if (review_goals.length === 0) {
            return true;
        }

        const payload = {
            session_id: parseInt(currentSessionId),
            review_goals,
        };

        // Amend the review a previous step of THIS update already wrote, rather
        // than appending a second line for the same user action.
        if (opts.reviewId) {
            payload.review_id = Number(opts.reviewId);
        }

        try {
            const response = await fetch(
                `${API_BASE}/therapist_api/insert_review_goals.php`,
                {
                    method: "POST",
                    headers: {
                        "Content-Type": "application/json",
                        Authorization: `Bearer ${token}`,
                    },
                    body: JSON.stringify(payload),
                }
            );
            const json = await response.json();
            if (!json.status) {
                throw new Error(json.message || "Save failed");
            }
            // Handed back so a follow-up step of the same update can amend this
            // review. 0 when the server did not report one, which falls the
            // caller back to appending.
            return { ok: true, reviewId: Number(json.review_id) || 0 };
        } catch (err) {
            console.error(`Failed to save goal review for goal ${goal.goal_id}:`, err.message);
            return { ok: false, reviewId: 0 };
        }
    };

    // -- Single "Update" button for the whole page: saves every goal --
    // One POST for the whole page, not one per goal. The endpoint already
    // stores every goal in the payload as a single review, and posting once
    // keeps that review complete. Posting per goal used to create one review
    // per goal, which left Client History with a separate line per goal
    // instead of one line per update.
    const handleUpdateAll = async () => {
        if (goals.length === 0) return;
        setIsSaving(true);
        try {
            // The reminder popup reads the target date from the saved goal rows.
            // Only re-check it when an edit actually moved a target date, so an
            // unrelated Update does not re-open a popup the therapist deferred.
            const targetDateChanged = goals.some((g) => {
                const e = goalEdits[g.goal_id] || {};
                if (e.targetDate === undefined || e.targetDate === null) return false;
                const nextDate = String(e.targetDate).slice(0, 10);
                const currentDate = String(g.target_date || "").slice(0, 10);
                return nextDate !== "" && nextDate !== currentDate;
            });

            const { ok, reviewId } = await saveGoalReview(goals[0]);
            if (ok) {
                // Remembered so the close confirmation amends THIS review rather
                // than appending a second line for the same Update.
                setLastReviewId(reviewId);
                setUpdateButtonText("Updated");
                setIsEditing(false);
                setToast({ message: "Goal review updated successfully.", type: "success" });

                // Decide from the values just submitted, BEFORE the refetch
                // replaces `goals` - otherwise the closure test would run against
                // data this handler is about to overwrite.
                const offerClose = hasClosableGoal(goals, goalEdits);

                // Refresh FIRST, then ask. fetchInterventions() flips
                // loadingInterventions, and this component early-returns a bare
                // "Loading goal review..." page while that is true, so opening the
                // popup without awaiting it unmounts the modal and makes it
                // reappear a beat later - which reads as the popup arriving before
                // the save. Awaiting guarantees the saved, refreshed form is on
                // screen first and the question is asked second.
                await fetchInterventions();

                // A target date edited here is now the goal's current date on
                // the server. Ask the global popup to re-read it so a date set
                // to today raises its reminder immediately.
                if (targetDateChanged) {
                    window.dispatchEvent(new Event("reminder-created"));
                }

                // A terminal status ("Achieved" / "Not achieved by target date")
                // ends the goal journey, so a successful Update that set one offers
                // the close confirmation. Update is now the ONLY route to this
                // popup - the separate Goal Closed button has been removed.
                if (offerClose) {
                    setShowCloseConfirm(true);
                }
            } else {
                setToast({ message: "Some goals failed to save. Please try again.", type: "error" });
            }
        } finally {
            setIsSaving(false);
        }
    };

    // -- Render --
    if (loadingInterventions) {
        return (
            <div className="grv-page">
                <div className="grv-header-bar">
                    <button className="grv-back-btn" onClick={() => navigate(-1)} aria-label="Go to previous page">
                        <FaArrowLeft aria-hidden="true" /> Previous page
                    </button>
                    <h1><span className="sr-only">{isHistoryView ? "Goal Review History: " : ""}</span>Goal Review</h1>
                </div>
                <p className="grv-loading" role="status" aria-live="polite">Loading goal review...</p>
                {toast && (
                    <Toast
                        message={toast.message}
                        type={toast.type}
                        onClose={() => setToast(null)}
                    />
                )}
            </div>
        );
    }

    const sessionInfo = interventionsData?.session;
    const allGoals = interventionsData?.goals || [];
    const reviewGoals = interventionsData?.review_goals || [];

    // SECTION 1 shows the NEWEST version of the goal, mirroring
    // get_goals_page.php so this page and the Review Existing Goals table
    // never disagree about which smart goal / target date / dosage is
    // current. When the planning goals were revised AFTER the last review
    // snapshot (/updategoal "Revise Selected Intervention"), they win - and
    // because they are fresh rows, the review-only fields below (outcome
    // measure evaluation, key components, actions) start blank instead of
    // carrying an old review's notes. History mode is untouched: it always
    // shows the exact requested version.
    const latestReview =
        reviewGoals.length > 0 ? reviewGoals[reviewGoals.length - 1] : null;
    const currentVersionGoals =
        latestReview && latestReview.goals && latestReview.goals.length > 0
            ? latestReview.goals
            : allGoals;
    // -- History mode: which saved version is on screen --
    const historyReview =
        isHistoryView && historyReviewId > 0
            ? reviewGoals.find(
                  (review) => Number(review.review_id) === historyReviewId
              ) || null
            : null;
    // History mode renders THAT exact saved version, not the latest one:
    // reviewId=0 shows the original Goal Set (planning goals), a positive
    // reviewId shows that specific review's snapshot. Every review was
    // appended with its own rows and goal_ids, so falling back to
    // currentVersionGoals displayed the LATEST review's data on every
    // history row no matter which row was clicked.
    const goals = isHistoryView
        ? historyReviewId === 0
            ? allGoals
            : historyReview?.goals?.length
              ? historyReview.goals
              : currentVersionGoals
        : newestVersionGoals(allGoals, reviewGoals);

    // -- First-time review vs re-visiting the same form --
    // When every goal on the current form has already been closed, the same form
    // has been finalized, so only the forward-navigation actions are shown. Edit /
    // Update / Updated stay available while the goals are still open (first review
    // of the planning goals or a new review cycle).
    const isFormClosed =
        goals.length > 0 && goals.every((g) => Number(g.is_closed) === 1);

    // -- A goal becomes read-only once it has been closed AND its status is
    // terminal. "Terminal" means EITHER "Achieved" OR "Not achieved by
    // target date" - this used to test `=== "Achieved"` only, so a goal closed as
    // "Not achieved" stayed fully editable and could be closed again. When every
    // goal on the form is locked the Update button is hidden and all inputs are
    // read-only. --
    const effectiveStatusOf = (goal) => {
        const edit = goalEdits[goal.goal_id] || {};
        return normalizeStatus(edit.status || goal.status || "");
    };

    const isLockedGoal = (goal) =>
        Number(goal.is_closed) === 1 && isTerminalStatus(effectiveStatusOf(goal));

    const isFormLocked = goals.length > 0 && goals.every(isLockedGoal);

    const historyLabel = isHistoryView
        ? historyReviewId === 0
            ? "Goal Set"
            : historyReview
              ? `Review ${historyReview.review_no}`
              : "Review"
        : "";

    if (!isHistoryView && (!sessionInfo || allGoals.length === 0)) {
        // -- "No, Skip." on the Intervention Planning popup --
        // From GoalsPage: go back the same way GoalsPage's "Previous page"
        // button does.  From start-assessment "Goal Progress
        // Update": behave exactly like this page's "Continue Pathway"
        // button (resume / create the client's review pathway).
        const handleNoGoalsSkip = () => {
            if (fromGoalsPage) {
                navigate("/Home");
                return;
            }
            const params = new URLSearchParams();
            if (reviewSessionId) params.set("sessionId", reviewSessionId);
            const resolveClientId = reviewClientId || sessionInfo?.client_id;
            if (resolveClientId) params.set("clientId", resolveClientId);
            let carryClient = sessionInfo?.client_name || "";
            let carryAge = reviewSessionAge || sessionInfo?.age_group || "";
            try {
                const stored = JSON.parse(localStorage.getItem("reviewCarryOver") || "null");
                if (stored && stored.session === "Review" && stored.client) {
                    const sameClient = !sessionInfo?.client_name ||
                        stored.client.trim().toLowerCase() === sessionInfo.client_name.trim().toLowerCase();
                    if (sameClient) {
                        carryClient = stored.client;
                        if (stored.ageRange) carryAge = stored.ageRange;
                    }
                }
            } catch (error) { /* ignore malformed stored value */ }
            if (carryClient) params.set("client", carryClient);
            if (carryAge) params.set("age", carryAge);
            params.set("fromGoalReview", "1");
            navigate(`/review-assessment?${params.toString()}`);
        };

        return (
            <div className="grv-page">
                <div className="grv-header-bar">
                    <button className="grv-back-btn" onClick={() => navigate(-1)} aria-label="Go to previous page">
                        <FaArrowLeft aria-hidden="true" /> Previous page
                    </button>
                    <h1><span className="sr-only">{isHistoryView ? "Goal Review History: " : ""}</span>Goal Review</h1>
                </div>
                <p className="grv-loading" role="status">No goals have been saved for this session.</p>

                {/* -- Popup: Intervention Planning not done for this client -- */}
                <div className="grv-modal-overlay" role="dialog" aria-modal="true" aria-label="Intervention Planning">
                    <div className="grv-modal-card">
                        <p className="grv-modal-text">
                            Intervention Planning not done for this Client. Do you want to set goal procedure now?
                        </p>
                        <div className="grv-modal-actions">
                            <button
                                className="grv-modal-yes"
                                disabled={!currentSessionId}
                                onClick={() => navigate(`/updategoal?sessionId=${currentSessionId}`)}
                            >
                                Yes, Set Goal Procedure
                            </button>
                            <button className="grv-modal-no" onClick={handleNoGoalsSkip}>
                                No, Skip.
                            </button>
                        </div>
                    </div>
                </div>

                {/* Modal styles duplicated here because the shared grv-modal-*
                    rules live inside the main return's style block, which is
                    not mounted in this early-return state. */}
                <style>{`
                    .grv-modal-overlay {
                        position: fixed;
                        inset: 0;
                        background: rgba(0, 0, 0, 0.45);
                        display: flex;
                        align-items: center;
                        justify-content: center;
                        z-index: 1000;
                        padding: 16px;
                    }
                    .grv-modal-card {
                        background: #fff;
                        border-radius: 14px;
                        width: 100%;
                        max-width: 360px;
                        padding: 28px 24px 22px;
                        text-align: center;
                        position: relative;
                        box-shadow: 0 8px 30px rgba(0, 0, 0, 0.25);
                    }
                    .grv-modal-text {
                        font-size: 14px;
                        color: #555;
                        line-height: 1.5;
                        margin: 0 0 22px;
                    }
                    .grv-modal-actions {
                        display: flex;
                        gap: 12px;
                        justify-content: center;
                    }
                    .grv-modal-yes,
                    .grv-modal-no {
                        border: none;
                        border-radius: 8px;
                        padding: 10px 18px;
                        font-size: 14px;
                        font-weight: 600;
                        cursor: pointer;
                        flex: 1;
                    }
                    .grv-modal-yes {
                        background: #247A33;
                        color: #fff;
                    }
                    .grv-modal-yes:disabled {
                        opacity: 0.6;
                        cursor: not-allowed;
                    }
                    .grv-modal-no {
                        background: #d32f2f;
                        color: #fff;
                    }
                `}</style>
                {toast && (
                    <Toast
                        message={toast.message}
                        type={toast.type}
                        onClose={() => setToast(null)}
                    />
                )}
            </div>
        );
    }

    return (
        <>
            {/* ------------------------------------------------------
                SECTION 1 ? GOAL REVIEW SUMMARY
                Displays the summary of the session's assessment findings
                and goals pulled from get_test_intervention.
                ------------------------------------------------------ */}
            <div className="grv-page">
                <div className="grv-header-bar">
                    <button className="grv-back-btn2" onClick={() => navigate(-1)} aria-label="Go to previous page">
                        <FaArrowLeft aria-hidden="true" /> Previous page
                    </button>
                    <h1 ><span className="sr-only">{isHistoryView ? "Goal Review History: " : ""}</span>Goal Review</h1>
                </div>

                <div className="grv-content">
                    <div className="summ-container11">
                        <div className="sum-cont11">

                            {/* Section 1 - Row 1: Client name (centered, cpa color) */}
                            <div className="summ-client-row">
                                {interventionsData?.session?.client_name || "Client"}
                            </div>

                            {/* Section 1 - Row 2: Metadata pills */}
                            <div className="summ-meta-row" >
                                <span className="summ-meta-pill summ-meta-plain">
                                    <span className="summ-meta-label" style={{ color: "#247A33", fontSize: "16px" }}>Assessment Date:</span>&nbsp;
                                    <span className="summ-meta-value" style={{ color: "#247A33" }}>
                                        {(() => {
                                            const code = String(interventionsData?.session?.client_code || "");
                                            const digits = code.replace(/\D/g, "").slice(0, 8);
                                            if (digits.length === 8) {
                                                const dd = digits.slice(0, 2);
                                                const mm = digits.slice(2, 4);
                                                const yyyy = digits.slice(4, 8);
                                                const months = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
                                                const mIdx = parseInt(mm, 10) - 1;
                                                return `${parseInt(dd, 10)} ${months[mIdx]} ${yyyy}`;
                                            }
                                            return "";
                                        })()}
                                    </span>
                                </span>
                                <span className="summ-meta-pill summ-meta-green">
                                    <span className="summ-meta-label" style={{ color: "#626A6F" }}>Client ID:</span>&nbsp;
                                    <span className="summ-meta-value" style={{ color: "#247A33" }}>{interventionsData?.session?.client_code || ""}</span>
                                </span>
                                <span className="summ-meta-pill summ-meta-green">
                                    <span className="summ-meta-label" style={{ color: "#626A6F" }}>Age Group:</span>&nbsp;
                                    <span className="summ-meta-value" style={{ color: "#247A33" }}>{interventionsData?.session?.age_group || ""}</span>
                                </span>
                                {isHistoryView && (
                                    <span className="summ-meta-pill summ-meta-green">
                                        <span className="summ-meta-label" style={{ color: "#626A6F" }}>Version:</span>&nbsp;
                                        <span className="summ-meta-value" style={{ color: "#247A33" }}>{historyLabel} (read only)</span>
                                    </span>
                                )}
                            </div>

                            {/* Section 1 - Row 3: Section title */}
                            <h3 className="summ-section-title">Interpreting Medical History and Combined Assessment Findings</h3>

                            {/* Section 1 - Row 4: Topography + Severity */}
                            <div className="summ-findings-block">
                                <div className="summ-finding-row">
                                    <span className="icon-circle1"><FontAwesomeIcon icon={faLocationDot} aria-hidden="true" /></span>
                                    <strong className="summ-finding-label">Topography:&nbsp;</strong>
                                    <span className="summ-value-pill">{interventionsData?.session?.topography || ""}</span>
                                </div>
                                <div className="summ-finding-row">
                                    <span className="icon-circle1"><FontAwesomeIcon icon={faSliders} aria-hidden="true" /></span>
                                    <strong className="summ-finding-label">Severity:&nbsp;</strong>
                                    <span className="summ-value-pill">{interventionsData?.session?.severity || ""}</span>
                                </div>
                            </div>

                            {/* Section 1 - Row 5: Early indicators of comorbidities */}
                            <div className="summ-finding-row">
                                <span className="icon-circle1"><FontAwesomeIcon icon={faCircleExclamation} aria-hidden="true" /></span>
                                <strong className="summ-finding-label">Early indicators of comorbidities:&nbsp;</strong>
                                <span className="summ-value-pill">
                                    {interventionsData?.session?.comorbidities?.length
                                        ? interventionsData.session.comorbidities.map(item => item.type).join(", ")
                                        : ""}
                                </span>
                            </div>
                            <div className="summ-finding-row">
                                <span className="icon-circle1"><FontAwesomeIcon icon={faBullseye} aria-hidden="true" /></span>
                                <strong className="summ-finding-label">Goal Limiting Factors:&nbsp;</strong>
                                <span className="summ-value-pill">
                                    {(() => {
                                        const factors = interventionsData?.session?.goal_limiting_factors || [];
                                        return factors.join(", ");
                                    })()}
                                </span>
                            </div>

                            {/* Section 1 - Row 6: Goals */}
                            <div className="summ-finding-row">
                                <span className="icon-circle1"><FontAwesomeIcon icon={faBullseye} aria-hidden="true" /></span>
                                <strong className="summ-finding-label">Goals:&nbsp;</strong>
                                <span className="summ-value-pill">
                                    {buildDomainTagsFromDataMap(interventionsData?.data).join(", ")}
                                </span>
                            </div>
                        </div>
                    </div>

                    {goals.map((goal, gIndex) => {
                        const edit = goalEdits[goal.goal_id] || {};
                        /* Not every payload shape is an array (saved review
                           goals can omit the key or send an object), and
                           `.map` on a non-array would throw and blank the
                           whole card. Normalise it so the no-data placeholder
                           below is always reachable. */
                        const goalInterventions = Array.isArray(goal.interventions)
                            ? goal.interventions.filter(Boolean)
                            : [];
                        /* Every block below used to sit behind a
                           `hasAnySelection` gate, so a goal whose caregiver
                           picked nothing collapsed to a single placeholder line
                           and hid the dosage counters, the outcome measure, the
                           key components and the reflection prompts. That gate
                           was wrong for three of those four: outcome measure and
                           the reflection prompts are recorded per GOAL and need
                           no intervention at all. It was only ever defensible for
                           the dosage counters, which are keyed by intervention
                           row - and those are still reachable, because a goal
                           with no row now renders ONE blank block.

                           14 goals in this DB have no `pathway_goal_interventions`
                           row at all (not a row with an empty selection), so
                           this was not an edge case. */
                        /* One entry per real intervention row, plus a single blank
                           entry when the goal has none. "Dosage of Home Practice"
                           and "Therapists Sessions to Support this Goal" are
                           prescribed for the GOAL, so they must stay reachable
                           and editable even when no named intervention was
                           chosen. `null` is a legal entry: the render reads it
                           defensively. */
                        const interventionBlocks = goalInterventions.length
                            ? goalInterventions
                            : [null];
                        const isLocked = isLockedGoal(goal);
                        const isLastGoal = gIndex === goals.length - 1;

                        return (
                            <div
                                className={`grv-goal-block${isLocked ? " grv-goal-locked" : ""}${isLastGoal ? " grv-goal-block-last" : ""}`}
                                key={goal.goal_id}
                            >
                                <div className="grv-row-2col">
                                    <div className="grv-field">
                                        <label>Goal Domain </label>
                                        <div className="grv-box" style={{ padding: "7px 10px" }}>
                                            <div style={{ display: "flex", flexWrap: "wrap", gap: "5px" }}>
                                                {buildDomainTagsFromGoal(goal).map((tag, i) => (
                                                    <span key={i} style={{ background: "#e0e0e0", color: "#323C43", borderRadius: "4px", padding: "2px 10px", fontSize: "13px", fontWeight: "600", display: "inline-block" }}>{tag}</span>
                                                ))}
                                            </div>
                                        </div>
                                    </div>
                                    <div className="grv-field">
                                        <label>Goal Target Date</label>
                                        {isEditing && !isLocked ? (
                                            <div className="grv-date-wrapper">
                                                <input
                                                    type="date"
                                                    className="grv-box grv-date-input"
                                                    value={edit.targetDate || (goal.target_date || "").slice(0, 10)}
                                                    onChange={(e) =>
                                                        updateGoalEdit(goal.goal_id, "targetDate", e.target.value)
                                                    }
                                                    aria-label="Goal target date"
                                                />
                                                <FaCalendarAlt className="grv-date-icon" aria-hidden="true"
                                                    onClick={() => document.querySelector(".grv-date-wrapper input[type='date']")?.showPicker()} />
                                            </div>
                                        ) : (
                                            <div className="grv-box">{formatDateDisplay(edit.targetDate || goal.target_date)}</div>
                                        )}
                                    </div>
                                </div>

                                <div className="grv-field full">
                                    <label>Caregiver-led SMART goal</label>
                                    {isEditing && !isLocked ? (
                                        <input
                                            type="text"
                                            className="grv-select"
                                            value={edit.smartGoal || goal.smart_goal || ""}
                                            onChange={(e) =>
                                                updateGoalEdit(goal.goal_id, "smartGoal", e.target.value)
                                            }
                                            aria-label="Caregiver-led SMART goal"
                                        />
                                    ) : (
                                        <div className="grv-box multiline">{goal.smart_goal || "-"}</div>
                                    )}
                                </div>

                                <div className="grv-row-2col grv-status-date-row">
                                    <div className="grv-field">
                                        <label>Goal Status</label>
                                        <p className="grv-subheading">
                                            Please update the current status of the caregiver-led SMART goal.
                                        </p>
                                        <select
                                            className="grv-select"
                                            value={edit.status || STATUS_OPTIONS[0]}
                                            onChange={(e) =>
                                                updateGoalEdit(goal.goal_id, "status", e.target.value)
                                            }
                                            disabled={!isEditing || isLocked}
                                            aria-label={`Goal status for ${cleanGoalDomain(goal.goal_domain)}`}
                                        >
                                            {/*
                                              A saved status can legitimately be
                                              the "Select" placeholder (the Goal
                                              Set row's planning goal never had
                                              one chosen). STATUS_OPTIONS holds
                                              only real statuses, so a controlled
                                              select with that value silently
                                              displayed the FIRST option instead -
                                              showing "In progress - on track" for
                                              a goal that was never given a
                                              status. Render the saved value as
                                              its own option so history mode
                                              shows what was actually stored.
                                            */}
                                            {edit.status &&
                                                !STATUS_OPTIONS.includes(
                                                    edit.status
                                                ) && (
                                                    <option value={edit.status}>
                                                        {edit.status}
                                                    </option>
                                                )}
                                            {STATUS_OPTIONS.map((opt) => (
                                                <option key={opt} value={opt}>
                                                    {opt}
                                                </option>
                                            ))}
                                        </select>
                                    </div>
                                    <div className="grv-field" style={{ display: "none" }}>
                                        <label>Result Date</label>
                                        <input
                                            type="date"
                                            className="grv-box grv-date-input"
                                            aria-label="Result Date"
                                            value={edit.resultDate || ""}
                                            onChange={(e) =>
                                                updateGoalEdit(goal.goal_id, "resultDate", e.target.value)
                                            }
                                            readOnly={!isEditing || isLocked}
                                        />
                                    </div>
                                </div>

                                <div className="grv-status-note">
                                    {STATUS_DESCRIPTIONS[normalizeStatus(edit.status)] || STATUS_DESCRIPTIONS[STATUS_OPTIONS[0]]}
                                </div>

                                <label className="grv-label">
                                    What intervention/s has been selected by caregivers?
                                </label>


                                {interventionBlocks.map((iv, idx) => {
                                    const editKey = `${goal.goal_id}-${idx}`;
                                    const ivEdit = interventionEdits[editKey] || {};

                                    return (
                                        <div className="grv-intervention-block" key={editKey}>
                                            <div className="grv-selection-list">
                                                {(() => {
                                                    const raw = iv?.selected_intervention;
                                                    const selections = Array.isArray(raw)
                                                        ? raw.map((s) => String(s || "").trim()).filter(Boolean)
                                                        : typeof raw === "string" && raw.trim()
                                                            ? raw.split(",").map(s => s.trim()).filter(Boolean)
                                                            : [];
                                                    if (selections.length === 0) {
                                                        return (
                                                            <div style={{ padding: "10px 14px", fontSize: 14, color: "var(--cpa-gray-2, #626A6F)" }}>
                                                                No selected Interventions
                                                            </div>
                                                        );
                                                    }
                                                    return selections.map((item, i2) => (
                                                        <label
                                                            className="grv-selection-option"
                                                            key={i2}
                                                            style={{
                                                                color: "#323C43",
                                                                fontWeight: 700,
                                                                background: "#EBF7E9",
                                                                cursor: isLocked ? "default" : "pointer",
                                                            }}
                                                        >
                                                            <input
                                                                type="checkbox"
                                                                checked
                                                                onChange={() => {}}
                                                                disabled={!isEditing || isLocked}
                                                                aria-label={item}
                                                            />
                                                            <span style={{ color: "#323C43", fontWeight: 700 }}>{item}</span>
                                                        </label>
                                                    ));
                                                })()}
                                            </div>


                                            <div className="grv-sub-title">Dosage of Home Practice to Support this Goal</div>
                                            <p className="grv-sub-note">Review the dosage of intervention that was recommended to achieve this goal. Modify the fields below to best reflect the dosage that was achieved.</p>
                                            <div className="grv-counters-3col">
                                                <Counter
                                                    label="Number of minutes per day"
                                                    value={ivEdit.minutes ?? 0}
                                                    onChange={(v) => updateInterventionEdit(editKey, "minutes", v)}
                                                    disabled={!isEditing || isLocked}
                                                />
                                                <Counter
                                                    label="Number of weeks"
                                                    value={ivEdit.weeks ?? 0}
                                                    onChange={(v) => updateInterventionEdit(editKey, "weeks", v)}
                                                    disabled={!isEditing || isLocked}
                                                />
                                            </div>

                                            <div className="grv-sub-title">Therapists Sessions to Support this Goal</div>
                                            <div className="grv-counters-1col">
                                                <Counter
                                                    label="Number of Sessions"
                                                    value={ivEdit.sessions ?? 0}
                                                    onChange={(v) => updateInterventionEdit(editKey, "sessions", v)}
                                                    disabled={!isEditing || isLocked}
                                                />
                                            </div>
                                        </div>
                                    );
                                })}

                                {(
                                    <>
                                        <div className="grv-outcome-2col">
                                            <div className="grv-outcome-col">
                                                <label className="grv-label">Outcome measure used</label>
                                                <div className="grv-box multiline">{goal.outcome_measure || "-"}</div>
                                            </div>
                                            <div className="grv-outcome-col">
                                                <label className="grv-label">Outcome Measure Evaluation</label>
                                                <textarea
                                                    className="grv-box grv-outcome-eval"
                                                    aria-label="Outcome Measure Evaluation"
                                                    placeholder="Insert any follow up outcome ratings below:"
                                                    value={edit.outcomeFollowUp || ""}
                                                    onChange={(e) =>
                                                        updateGoalEdit(
                                                            goal.goal_id,
                                                            "outcomeFollowUp",
                                                            e.target.value
                                                        )
                                                    }
                                                    readOnly={!isEditing || isLocked}
                                                />
                                            </div>
                                        </div>
                                        <h3 className="grv-section-title">
                                            Review Key Components of Intervention Offered, Mode and Dose:
                                        </h3>
                                        {(() => {
                                            const sheets = getSelectedInterventions(goal);
                                            // One tab per "family" returned by get_key_components.php
                                            // (deduped, order follows the selected interventions).
                                            const kcEntries = [];
                                            const seenFamilies = new Set();
                                            sheets.forEach((sheet) => {
                                                const data = keyComponentsMap[sheet];
                                                if (!data) return;
                                                if (seenFamilies.has(data.family)) return;
                                                seenFamilies.add(data.family);
                                                kcEntries.push(data);
                                            });

                                            /* No tab can be built without a selected
                                               intervention to look the family up
                                               by, so say so rather than leaving
                                               the heading sitting over nothing -
                                               the same treatment the caregiver
                                               selection list gets. */
                                            if (kcEntries.length === 0) {
                                                return (
                                                    <div
                                                        style={{
                                                            padding: "10px 14px",
                                                            fontSize: 14,
                                                            color: "var(--cpa-gray-2, #626A6F)",
                                                        }}
                                                    >
                                                        No key components to review - no
                                                        intervention was selected for this
                                                        goal.
                                                    </div>
                                                );
                                            }

                                            // Default to the FIRST tab until the user picks one
                                            const selectedFamily =
                                                activeKcTab[goal.goal_id] &&
                                                    kcEntries.some(
                                                        (e) => e.family === activeKcTab[goal.goal_id]
                                                    )
                                                    ? activeKcTab[goal.goal_id]
                                                    : kcEntries[0].family;
                                            const active = kcEntries.find(
                                                (e) => e.family === selectedFamily
                                            );

                                            return (
                                                <>
                                                    <div
                                                        style={{
                                                            display: "flex",
                                                            flexWrap: "wrap",
                                                            gap: "8px",
                                                            margin: "10px 0 12px",
                                                        }}
                                                    >
                                                        {kcEntries.map((entry) => (
                                                            <button
                                                                key={entry.family}
                                                                type="button"
                                                                className={`toggle-btn3 grv-kc-tab ${selectedFamily === entry.family ? "active" : ""
                                                                    }`}
                                                                onClick={() =>
                                                                    setActiveKcTab((prev) => ({
                                                                        ...prev,
                                                                        [goal.goal_id]: entry.family,
                                                                    }))
                                                                }
                                                            >
                                                                {entry.family}
                                                            </button>
                                                        ))}
                                                    </div>
                                                    <div className="grv-kc-panel">
                                                        {active.title && (
                                                            <h4 className="grv-principles-title">
                                                                {active.title}
                                                            </h4>
                                                        )}
                                                        {active.description && (
                                                            <div
                                                                className="grv-principle-text"
                                                                dangerouslySetInnerHTML={{
                                                                    __html: active.description,
                                                                }}
                                                            />
                                                        )}
                                                    </div>
                                                </>
                                            );
                                        })()}

                                        <p className="grv-reflect-instructions">
                                            Reflect on the intervention as a whole and all of the key components.
                                            You do not need to rate each component separately. Use the prompts
                                            below to identify strengths, areas for improvement, and next steps.
                                        </p>

                                        <div className="grv-field-inline">
                                            <label>What key components were demonstrated well?</label>
                                            <textarea
                                                aria-label="What key components were demonstrated well?"
                                                placeholder="Type Here."
                                                value={edit.demonstratedWell || ""}
                                                onChange={(e) =>
                                                    updateGoalEdit(
                                                        goal.goal_id,
                                                        "demonstratedWell",
                                                        e.target.value
                                                    )
                                                }
                                                readOnly={!isEditing || isLocked}
                                            />
                                        </div>
                                        <div className="grv-field-inline">
                                            <label>What key components could be strengthened further?</label>
                                            <textarea
                                                aria-label="What key components could be strengthened further?"
                                                placeholder="Type Here..."
                                                value={edit.strengthened || ""}
                                                onChange={(e) =>
                                                    updateGoalEdit(goal.goal_id, "strengthened", e.target.value)
                                                }
                                                readOnly={!isEditing || isLocked}
                                            />
                                        </div>
                                        <div className="grv-field-inline">
                                            <label>
                                                Note any specific actions you can change when delivering this
                                                intervention.
                                            </label>
                                            <textarea
                                                aria-label="Note any specific actions you can change when delivering this intervention."
                                                placeholder="Type Here..."
                                                value={edit.actions || ""}
                                                onChange={(e) =>
                                                    updateGoalEdit(goal.goal_id, "actions", e.target.value)
                                                }
                                                readOnly={!isEditing || isLocked}
                                            />
                                        </div>
                                    </>
                                )}
                            </div>
                        );
                    })}

                    <div className="grv-footer">
                        {isFormLocked || goalClosedDate ? (
                            <button className="grv-close-btn" disabled style={{ opacity: 0.7, cursor: "not-allowed" }}>
                                Goal closed on {goalClosedDate || (() => {
                                    const closedGoal = goals.find(g => Number(g.is_closed) === 1);
                                    if (closedGoal && closedGoal.result_date) return formatClosedDate(closedGoal.result_date);
                                    return formatDateDisplay(getTodayDateString());
                                })()}
                            </button>
                        ) : (
                            <>
                                {/* History mode is a read-only snapshot, so every
                                    action that changes or advances the goals is
                                    removed. Only Exit stays. */}
                                {!isHistoryView && (
                                <>
                                <button
                                    className="grv-review-btn"
                                    onClick={async () => {
                                        // Show the blocking spinner BEFORE the first
                                        // await, otherwise the context fetch runs
                                        // with nothing on screen and the user only
                                        // sees the blank frame afterwards.
                                        setIsStartingAssessment(true);
                                        const startedAt = Date.now();

                                        try {
                                            if (currentSessionId && Object.keys(goalEdits).length > 0) {
                                                const byOrder = {};
                                                const byGoalId = {};
                                                (goals || []).forEach((g, idx) => {
                                                    const e = goalEdits[g.goal_id];
                                                    if (e) {
                                                        const ord = Number(g.goal_order) || (idx + 1);
                                                        byOrder[ord] = e;
                                                        byGoalId[g.goal_id] = e;
                                                    }
                                                });
                                                localStorage.setItem(
                                                    `goalreview_draft_${currentSessionId}`,
                                                    JSON.stringify({
                                                        ...goalEdits,
                                                        byOrder,
                                                        byGoalId,
                                                    })
                                                );
                                            }

                                            // If in editing mode, persist the review to the server
                                            // before launching start-assessment so the database has
                                            // the latest review notes!
                                            if (isEditing && goals.length > 0) {
                                                const { ok } = await saveGoalReview(
                                                    goals[0],
                                                    lastReviewId ? { reviewId: lastReviewId } : {}
                                                );
                                                // saveGoalReview swallows server errors into
                                                // { ok: false }, so without this the notes the
                                                // therapist just typed were dropped silently and
                                                // the Review Smart Goal snapshot that follows
                                                // carried NOTHING forward - the new Client
                                                // History row then showed blank review fields.
                                                if (!ok) {
                                                    setToast({
                                                        message:
                                                            "Goal review notes could not be saved to the server. Please click Update and try again.",
                                                        type: "error",
                                                    });
                                                }
                                            }
                                        } catch (e) {
                                            console.error("Error persisting draft or review before starting assessment:", e);
                                        }

                                        // Entry point #3: the Review Smart Goal
                                        // pathway. Carries the client identity,
                                        // the age group and the originating
                                        // session (the one holding the goal being
                                        // reviewed) so start-assessment can
                                        // prefill and later revise the right row.
                                        const params = new URLSearchParams();
                                        if (sessionInfo?.client_name) params.set("client", sessionInfo.client_name);
                                        params.set("appointment", "Review Smart Goal");
                                        params.set("fromGoalReview", "1");
                                        const resolveClientId = reviewClientId || sessionInfo?.client_id;
                                        if (resolveClientId) params.set("clientId", resolveClientId);
                                        /*
                                         * Age Group of the EXACT session the
                                         * therapist clicked Review on wins: it
                                         * is what /start-assessment must show
                                         * (Appointment / Smart Goal / Client /
                                         * Age Group of that session), what the
                                         * missing-pathway check is judged
                                         * against, and (as age_group below)
                                         * the band the resume target must sit
                                         * on. reviewSessionAge - the client's
                                         * newest review session, possibly on
                                         * ANOTHER band - is only a fallback
                                         * for rows whose session has no band
                                         * recorded.
                                         */
                                        const resolveAge = sessionInfo?.age_group || reviewSessionAge;
                                        if (resolveAge) params.set("age", resolveAge);
                                        /*
                                         * goalSessionId names the session that
                                         * owns the goals shown on this page
                                         * (currentSessionId). The therapist got
                                         * here from a specific row of "Review
                                         * Existing Goals", so those goals are the
                                         * ones being reviewed - whether the row
                                         * sits on an Initial session or on a
                                         * Review session that itself saved the
                                         * goal (a goal-review row). Passing it
                                         * lets /start-assessment prefill from
                                         * exactly that session and later revise
                                         * that same goal, instead of inheriting
                                         * from an unrelated session's goals.
                                         */
                                        if (currentSessionId) {
                                            params.set("goalSessionId", String(currentSessionId));
                                        }

                                        /*
                                         * CORRECTION: "Review Assessment" always starts a BRAND NEW
                                         * Review Smart Goal session for this goalSessionId.
                                         *
                                         * Previously this block called get_smart_goal_context.php to
                                         * find an existing RSG draft and passed its session_id as
                                         * ?sessionId=, which resumed the old mid-question answers.
                                         * The spec says each "Review Assessment" click must open a
                                         * fresh RSG pathway - old RSG data should never be pre-filled
                                         * when starting from goalreview.  No ?sessionId= is therefore
                                         * sent; start-assessment creates a new session from scratch.
                                         */

                                        /*
                                         * Hold the spinner for a beat before
                                         * navigating. Even without the async
                                         * context fetch the overlay should be
                                         * visible long enough for the user to
                                         * register the action (450 ms minimum).
                                         */
                                        const MIN_VISIBLE_MS = 450;
                                        const elapsed = Date.now() - startedAt;
                                        if (elapsed < MIN_VISIBLE_MS) {
                                            await new Promise((r) => setTimeout(r, MIN_VISIBLE_MS - elapsed));
                                        }

                                        navigate(`/start-assessment?${params.toString()}`);
                                        // navigate() unmounts this component, which
                                        // takes the overlay with it. Reset anyway so
                                        // a re-mount never inherits a stuck spinner.
                                        setIsStartingAssessment(false);
                                    }}
                                    disabled={isSaving || isStartingAssessment}
                                >
                                    Review Assessment
                                </button>
                                {/* Once every goal is closed the review is finalized: hide Edit / Update /
                                    Updated instead of leaving them clickable. They
                                    used to render unconditionally, so the comment
                                    above isFormClosed claimed this already happened
                                    when it did not. "Continue Pathway" and "Review
                                    Assessment" deliberately stay: those move the
                                    therapist forward, and hiding them too would
                                    strand the page with only "Previous page".
                                    Closing the goal is no longer a separate action -
                                    Update -> popup -> Yes is the only path. */}
                                {!isFormClosed && (
                                    <>
                                        {!isEditing ? (
                                            <button
                                                className="grv-update-btn"
                                                onClick={() => { setIsEditing(true); setUpdateButtonText("Update"); }}
                                                disabled={isSaving}
                                            >
                                                Edit
                                            </button>
                                        ) : (
                                            <button
                                                className="grv-update-btn"
                                                onClick={handleUpdateAll}
                                                disabled={isSaving}
                                            >
                                                {isSaving ? "Saving.." : updateButtonText}
                                                <FaSyncAlt className={isSaving ? "grv-spin" : ""} aria-hidden="true" />
                                            </button>
                                        )}
                                        {!isEditing && updateButtonText === "Updated" && (
                                            <button className="grv-update-btn" disabled style={{ opacity: 0.7, cursor: "not-allowed" }}>
                                                Updated
                                            </button>
                                        )}
                                    </>
                                )}
                                {!fromGoalsPage && (
                                    <button
                                        className="grv-close-btn"
                                        onClick={() => {
                                            const params = new URLSearchParams();
                                            if (reviewSessionId) params.set("sessionId", reviewSessionId);
                                            const resolveClientId = reviewClientId || sessionInfo?.client_id;
                                            if (resolveClientId) params.set("clientId", resolveClientId);
                                            let carryClient = sessionInfo?.client_name || "";
                                            let carryAge = reviewSessionAge || sessionInfo?.age_group || "";
                                            try {
                                                const stored = JSON.parse(localStorage.getItem("reviewCarryOver") || "null");
                                                if (stored && stored.session === "Review" && stored.client) {
                                                    const sameClient = !sessionInfo?.client_name ||
                                                        stored.client.trim().toLowerCase() === sessionInfo.client_name.trim().toLowerCase();
                                                    if (sameClient) {
                                                        carryClient = stored.client;
                                                        if (stored.ageRange) carryAge = stored.ageRange;
                                                    }
                                                }
                                            } catch (error) { /* ignore malformed stored value */ }
                                            if (carryClient) params.set("client", carryClient);
                                            if (carryAge) params.set("age", carryAge);
                                            params.set("fromGoalReview", "1");

                                            // All appointments are driven from
                                            // /start-assessment, so that is where
                                            // this returns. It used to point at the
                                            // standalone /review-assessment page,
                                            // which cannot tell a Review Smart
                                            // Goal from a plain Review - an RSG
                                            // session therefore reopened in the
                                            // wrong mode and lost its goal context.
                                            //
                                            // The `appointment` param is required:
                                            // without it the page opens the Initial
                                            // step-1 form.
                                            const isSmartGoalSession = reviewApptType === "review smart goal";
                                            params.set("appointment", isSmartGoalSession ? "Review Smart Goal" : "Review");
                                            // Carry the goal being revised, so
                                            // /start-assessment re-fetches it and
                                            // the RSG step-1 card can show it.
                                            if (isSmartGoalSession) {
                                                let carriedGoalSessionId = "";
                                                try {
                                                    const raw = JSON.parse(localStorage.getItem("reviewCarryOver") || "null");
                                                    carriedGoalSessionId = raw?.goalSessionId ? String(raw.goalSessionId) : "";
                                                } catch (error) { /* ignore malformed stored value */ }
                                                if (carriedGoalSessionId) params.set("goalSessionId", carriedGoalSessionId);
                                            }
                                            navigate(`/start-assessment?${params.toString()}`);
                                        }}
                                        disabled={isSaving}
                                    >
                                        Continue Pathway
                                    </button>
                                )}
                                </>
                                )}
                                <button
                                    className="grv-close-btn"
                                    onClick={() => navigate("/Home")}
                                    disabled={isSaving}
                                >
                                    Exit
                                </button>
                            </>
                        )}
                    </div>
                </div>
            </div>

            <PriviewsGoalReview sessionId={currentSessionId} />

            {toast && (
                <Toast
                    message={toast.message}
                    type={toast.type}
                    onClose={() => setToast(null)}
                />
            )}

            {/*
                * Rendered last so it stacks above the Toast and the close-confirm
                * modal. It is a sibling of the whole page rather than part of the
                * body, because the body early-returns a bare "Loading goal
                * review..." frame while loadingInterventions is true - anything
                * nested inside would be unmounted with it.
                */}
            {isStartingAssessment && <LoadingOverlay message="Opening Review Assessment, please wait..." />}

            {showCloseConfirm && (
                <div className="grv-modal-overlay" onClick={() => setShowCloseConfirm(false)} onKeyDown={(e) => { if (e.key === "Escape") { e.preventDefault(); setShowCloseConfirm(false); } }} role="dialog" aria-modal="true" aria-label="Confirm close goal">
                    <div className="grv-modal-card" onClick={(e) => e.stopPropagation()}>
                        <button
                            className="grv-modal-close-x"
                            onClick={() => setShowCloseConfirm(false)}
                            aria-label="Close"
                        >
                            &#10005;
                        </button>
                        <div className="grv-modal-icon">&#9888;</div>
                        <h3 className="grv-modal-title">Confirmation</h3>
                        <p className="grv-modal-text">
                            This action will end the goal journey. Are you sure to close this goal?
                        </p>
                        <div className="grv-modal-actions">
                            <button className="grv-modal-yes" onClick={confirmGoalClosed}>
                                Yes, Confirm
                            </button>
                            <button
                                className="grv-modal-no"
                                onClick={declineGoalClosed}
                            >
                                No, cancel
                            </button>
                        </div>
                    </div>
                </div>
            )}
            {/* -- Styles matching the design mock-up, shared by both sections -- */}
            <style>{`
                .grv-page {
                    width: 100%;
                    max-width: 1600px;
                    margin: 0 auto;
                    background: #ffffff;
                    min-height: 100vh;
                    padding-bottom: 40px;
                }
                .grv-page.getall {
                    margin-top: 24px;
                }
                .grv-readonly {
                    background: #eceeef !important;
                    color: #666 !important;
                    cursor: not-allowed;
                    user-select: text;
                }
                .grv-goal-locked input,
                .grv-goal-locked select,
                .grv-goal-locked textarea {
                    background: #e0e0e0 !important;
                    color: #323C43 !important;
                }
                .grv-header-bar {
                    position: relative;
                    background: #ffffff;
                    color: #247A33;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    padding: 14px 16px;
                    min-height: 50px;
                }
                .grv-header-bar h2 {
                    margin: 0;
                    font-size: 20px;
                    font-weight: 700;
                }
                .grv-back-btn {
                    position: absolute;
                    left: 12px;
                    top: 50%;
                    transform: translateY(-50%);
                    background: white;
                    border: 1px solid rgba(255,255,255,0.6);
                    color: #247A33;
                    border-radius: 5px;
                    padding: 6px 12px;
                    font-size: 13px;
                    display: flex;
                    align-items: center;
                    gap: 6px;
                    cursor: pointer;
                    font-weight: 700;
                }
                .grv-back-btn2 {
                    position: absolute;
                    left: 20px;
                    top: 50%;
                    transform: translateY(-50%);
                    display: inline-flex;
                    align-items: center;
                    gap: 8px;
                    background-color: #fff;
                    border: 1.5px solid #247A33;
                    color: #247A33;
                    font-size: 15px;
                    font-weight: 500;
                    padding: 8px 16px;
                    border-radius: 6px;
                    cursor: pointer;
                    text-decoration: none;
                    transition: all 0.2s ease-in-out;
                }
                .grv-back-btn2 .back-icon,
                .grv-back-btn2 svg {
                    font-size: 16px;
                }
                .grv-back-btn2:hover {
                    color: #247A33;
                    border-color: #047df6;
                    text-decoration: underline;
                }
                .grv-content {
                    padding: 18px 70px 10px;
                }
                .grv-loading {
                    padding: 24px 20px;
                    text-align: center;
                }
                .grv-client-name {
                    text-align: center;
                    color: #247A33;
                    font-size: 22px;
                    margin: 4px 0 2px;
                }
                .grv-assessment-date {
                    text-align: left;
                    color: #247A33;
                    font-weight: 600;
                    margin: 0 0 18px;
                    font-size:24px;
                }
                .grv-goal-block {
                    border-bottom: 2px solid #b6d7b6;
                    padding-bottom: 26px;
                    margin-bottom: 26px;
                }
                .grv-goal-block:last-child {
                    border-bottom: none;
                    margin-bottom: 0;
                    padding-bottom: 0;
                }
                .grv-goal-block.grv-goal-block-last {
                    border-bottom: none;
                    margin-bottom: 0;
                    padding-bottom: 0;
                }
                .grv-row-2col {
                    display: grid;
                    grid-template-columns: 1fr 1fr 1fr;
                    gap: 16px;
                    margin-bottom: 20px;
                }
                .grv-field {
                    display: flex;
                    flex-direction: column;
                }
                .grv-field.full {
                    margin-bottom: 20px;
                }
                .grv-field label,
                .grv-label {
                    font-size: 16px;
                    color: #247A33;
                    font-weight: 700;
                    margin-bottom: 6px;
                    display: block;
                }
                .grv-box {
                    border: 1px solid #cfd8dc;
                    border-radius: 8px;
                    padding: 9px 12px;
                    font-size: 14px;
                    background: #f0f0f0;
                    color: #555;
                }
                .grv-box.multiline {
                    min-height: 44px;
                }
                .grv-date-input {
                    width: 100%;
                    box-sizing: border-box;
                    font-family: inherit;
                    background: #fff;
                    color: #333;
                }
                .grv-date-wrapper {
                    position: relative;
                    display: flex;
                    align-items: center;
                }
                .grv-date-icon {
                    position: absolute;
                    right: 12px;
                    color: #247A33;
                    font-size: 16px;
                    cursor: pointer;
                    pointer-events: auto;
                    background: #fff;
                }
                .grv-select {
                    border: 1px solid #cfd8dc;
                    border-radius: 8px;
                    padding: 9px 12px;
                    font-size: 14px;
                    background: #fff;
                    color: #333;
                    width: 100%;
                    box-sizing: border-box;
                }
                .grv-subheading {
                    font-weight: 500;
                    font-style: italic;
                    color: #263238;
                    margin: 4px 0 12px;
                    font-size: 13px;
                }
                .grv-status-note {
                    background: #edeaea;
                    border: 1px solid #cfd8dc;
                    border-radius: 8px;
                    padding: 12px 14px;
                    font-size: 16px;
                    color: #333;
                    line-height: 1.6;
                    margin-bottom: 20px;
                }
                .intervention-tags1 {
                    display: flex;
                    flex-wrap: wrap;
                    gap: 8px;
                    margin: 10px 0 18px;
                }
                .intervention-tag1 {
                    background: #f0f0f0;
                    color: #247A33;
                    border: 1px solid #247A33;
                    border-radius: 16px;
                    padding: 6px 14px;
                    font-size: 13px;
                }
                .grv-intervention-block {
                    padding-top: 0px;
                    margin-top: 18px;
                    margin-buttom:20px;
                }
                .grv-intervention-row {
                    display: flex;
                    align-items: center;
                    gap: 8px;
                    margin-bottom: 20px;
                    font-weight: 700;
                    color: #247A33;
                }
                .grv-pill {
                    background: #f0f0f0;
                    color: #247A33;
                    border: 1px solid #247A33;
                    border-radius: 5px;
                    padding: 6px 14px;
                    font-size: 13px;
                }
                .grv-selection-list {
                    border: 1px solid #d5dbe0;
                    border-radius: 8px;
                    overflow: hidden;
                    margin-bottom: 20px;
                }
                .grv-selection-option {
                    display: flex;
                    align-items: flex-start;
                    gap: 10px;
                    padding: 10px 14px;
                    font-size: 14px;
                    line-height: 1.5;
                    cursor: pointer;
                    white-space: normal;
                    word-break: break-word;
                    border-bottom: 1px solid #eef0f2;
                }
                .grv-selection-option:last-child {
                    border-bottom: none;
                }
                .grv-selection-option input[type="checkbox"] {
                    margin: 3px 0 0;
                    flex-shrink: 0;
                    width: 16px;
                    height: 16px;
                }
                .grv-selection-option span {
                    flex: 1;
                    text-align: left;
                }
                .grv-sub-title {
                    font-weight: 700;
                    color: #247A33;
                    margin: 14px 0 10px;
                    font-size: 14px;
                    text-align: left;
                }
                .grv-sub-note {
                    font-size: 14px;
                    line-height: 1.5;
                    color: #030303;
                    margin: -4px 0 12px;
                }
                .grv-counters-3col {
                    display: grid;
                    grid-template-columns: 1fr;
                    gap: 4px;
                    margin-bottom: 4px;
                }
                .grv-counters-1col {
                    display: grid;
                    grid-template-columns: 1fr;
                    gap: 4px;
                }
                .grv-counter-field {
                    display: flex;
                    flex-direction: row;
                    align-items: center;
                    justify-content: space-between;
                    gap: 16px;
                    max-width: 320px;
                    margin-bottom: 20px;
                }
                .grv-counter-field label {
                    font-size: 13px;
                    color: #247A33;
                    font-weight: 700;
                    display: inline-block;
                }
                .grv-counter {
                    display: inline-flex;
                    align-items: center;
                    gap: 6px;
                    flex-shrink: 0;
                }
                .grv-counter-input {
                    border: 1px solid var(--cpa-gray-3, #737A7E);
                    border-radius: 5px;
                    background: #fff;
                    font-weight: 600;
                    color: #333;
                    width: 52px;
                    height: 39px;
                    box-sizing: border-box;
                    text-align: center;
                    font-size: 14px;
                    padding: 0;
                    font-family: inherit;
                    -moz-appearance: textfield;
                }
                .grv-counter-input::-webkit-outer-spin-button,
                .grv-counter-input::-webkit-inner-spin-button {
                    -webkit-appearance: none;
                    margin: 0;
                }
                .grv-counter button {
                    background: #0063A5;
                    color: #fff;
                    border: none;
                    border-radius: 6px;
                    width: 28px;
                    height: 39px;
                    box-sizing: border-box;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    cursor: pointer;
                    font-size: 18px;
                    font-weight: bold;
                    padding: 0;
                }
                .grv-counter button:disabled {
                    opacity: 0.5;
                    cursor: not-allowed;
                }
                .grv-counter button:first-child { border-top-right-radius: 0; border-bottom-right-radius: 0; }
                .grv-counter button:last-child { border-top-left-radius: 0; border-bottom-left-radius: 0; }
                .grv-section-title {
                    color: #247A33;
                    font-size: 24px;
                    padding: 22px 0 8px;
                }
                .grv-principles-title {
                    color: #247A33;
                    font-size: 20px;
                    padding: 0px 0 0px;
                }
                .grv-principle-text {
                    font-size: 16px;
                    line-height: 1.4;
                    color: #333;
                    margin: 0 0 10px;
                    text-align: justify;
                    white-space: pre-line;
                }
                .grv-principle-text p {
                    margin: 0 0 8px;
                    text-align: justify;
                }
                .grv-principle-text strong {
                    color: #247A33;
                }
                .grv-principle-text ul,
                .grv-principle-text ol {
                    margin: 0 0 8px;
                    padding-left: 22px;
                    text-align: justify;
                }
                .grv-principle-text li {
                    margin-bottom: 4px;
                }
                .grv-kc-panel {
                    margin-top: 4px;
                }
                .grv-kc-panel .grv-principles-title {
                    margin: 0 0 8px;
                    padding: 0;
                }
                .grv-principle-label {
                    font-size: 18px;
                    color: #247A33;
                    text-align: justify;
                }
                .grv-reflect-instructions {
                    text-align: center;
                    font-weight: 600;
                    font-size: 18px;
                    color: #263238;
                    padding: 30px 0px 18px 0px;
                }
                .grv-iv-tiles {
                    display: flex;
                    flex-wrap: wrap;
                    gap: 14px;
                    margin: 16px 0 6px;
                }
                .grv-iv-tile {
                    flex: 0 0 200px;
                    width: 200px;
                    border: 1px solid #e0e0e0;
                    border-radius: 8px;
                    overflow: hidden;
                    background: #fff;
                    transition: box-shadow 0.2s;
                    cursor: pointer;
                    text-align: center;
                }
                .grv-iv-tile:hover {
                    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
                }
                .grv-iv-tile-open {
                    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
                    border-color: #247A33;
                }
                .grv-iv-tile-image {
                    width: 100%;
                    height: 140px;
                    overflow: hidden;
                    background: #e8f5e9;
                }
                .grv-iv-tile-image img {
                    width: 100%;
                    height: 100%;
                    object-fit: cover;
                    display: block;
                }
                .grv-iv-tile-label {
                    padding: 12px 14px;
                    color: #247A33;
                    font-size: 14px;
                    font-weight: 700;
                    line-height: 1.4;
                    word-break: break-word;
                }
                .grv-iv-tile-details {
                    flex: 0 0 100%;
                    width: 100%;
                    display: flex;
                    gap: 20px;
                    background: #fff;
                    border: 1px solid #cfd8dc;
                    border-radius: 8px;
                    padding: 16px;
                    box-sizing: border-box;
                }
                .grv-iv-tile-details-image {
                    flex: 0 0 180px;
                }
                .grv-iv-tile-details-image img {
                    width: 100%;
                    border-radius: 8px;
                    border: 2px solid #2f7a3d;
                    display: block;
                }
                .grv-iv-tile-details-text {
                    flex: 1;
                    min-width: 0;
                }
                .grv-iv-tile-details-text h5 {
                    color: #247A33;
                    font-size: 16px;
                    font-weight: 700;
                    margin: 0 0 10px;
                }
                .grv-iv-tile-details-text p {
                    font-size: 15px;
                    line-height: 1.7;
                    color: #333;
                    margin: 0;
                    white-space: pre-line;
                    text-align: justify;
                }
                .grv-field textarea {
                    width: 100%;
                    box-sizing: border-box;
                    border: 1px solid #cfd8dc;
                    border-radius: 8px;
                    padding: 10px 12px;
                    font-size: 14px;
                    min-height: 60px;
                    resize: vertical;
                    font-family: inherit;
                    background: #fff;
                }
                .grv-field-inline {
                    display: flex;
                    align-items: flex-start;
                    gap: 20px;
                    margin-bottom: 20px;
                }
                .grv-field-inline label {
                    flex: 0 0 260px;
                    max-width: 260px;
                    font-size: 15px;
                    color: #247A33;
                    font-weight: 700;
                    line-height: 1.4;
                }
                .grv-field-inline textarea,
                .grv-field-inline .grv-box {
                    flex: 1;
                    box-sizing: border-box;
                    border: 1px solid #cfd8dc;
                    border-radius: 8px;
                    padding: 12px 14px;
                    font-size: 14px;
                    min-height: 72px;
                    resize: vertical;
                    font-family: inherit;
                    background: #fff;
                }
                @media (max-width: 700px) {
                    .grv-field-inline {
                        flex-direction: column;
                    }
                    .grv-field-inline label {
                        flex-basis: auto;
                        max-width: 100%;
                    }
                    .grv-iv-tile {
                        flex: 1 1 150px;
                        width: auto;
                        max-width: 200px;
                    }
                    .grv-iv-tile-details {
                        flex-direction: column;
                    }
                    .grv-iv-tile-details-image {
                        flex: 0 0 auto;
                        max-width: 220px;
                    }
                }
                .grv-outcome-2col {
                    display: grid;
                    grid-template-columns: 1fr 1fr;
                    gap: 20px;
                    align-items: stretch;
                    margin-top: 20px;
                }
                .grv-outcome-col {
                    display: flex;
                    flex-direction: column;
                }
                .grv-outcome-col > .grv-box,
                .grv-outcome-col > textarea.grv-box {
                    flex: 1 1 auto;
                    min-height: 56px;
                    width: 100%;
                    box-sizing: border-box;
                }
                .grv-outcome-eval {
                    background: #ffffff;
                    resize: vertical;
                }
                .grv-outcome-box {
                    border: 1px solid #90a4ae;
                    border-radius: 8px;
                    padding: 14px;
                    margin-top: 20px;
                    background: #fff;
                }
                .grv-outcome-box label {
                    font-weight: 700;
                    color: #247A33;
                    font-size: 13px;
                    display: block;
                    margin-bottom: 8px;
                }
                .grv-outcome-box textarea {
                    width: 100%;
                    box-sizing: border-box;
                    border: 1px solid #cfd8dc;
                    border-radius: 8px;
                    padding: 10px 12px;
                    font-size: 14px;
                    min-height: 50px;
                    resize: vertical;
                    font-family: inherit;
                }
                .grv-kc-tab {
                    font-family: "Foco", "Segoe UI", Arial, sans-serif;
                    font-weight: 700;
                    color: #323C43;
                    border-radius: 4px;
                }
                .grv-kc-tab.active {
                    background-color: #247A33;
                    color: #ffffff;
                    border-color: #247A33;
                    border-radius: 4px;
                }
                .grv-footer {
                    display: flex;
                    justify-content: flex-end;
                    gap: 12px;
                    margin-top: 26px;
                }
                .grv-review-btn {
                    background: #fff;
                    color: #247A33;
                    border: 1px solid #247A33;
                    border-radius: 4px;
                    padding: 10px 18px;
                    font-size: 14px;
                    font-weight: 600;
                    cursor: pointer;
                }
                .grv-review-btn:hover {
                    border-color: #0095C7;
                    color: #247A33;
                    text-decoration: underline;
                }
                .grv-update-btn {
                    background: #247A33;
                    color: #fff;
                    border: none;
                    border-radius: 8px;
                    padding: 10px 22px;
                    font-size: 14px;
                    font-weight: 600;
                    display: flex;
                    align-items: center;
                    gap: 8px;
                    cursor: pointer;
                }
                .grv-close-btn {
                    background: #fff;
                    color: #247A33;
                    border: 1px solid #247A33;
                    border-radius: 8px;
                    padding: 10px 18px;
                    font-size: 14px;
                    font-weight: 600;
                    cursor: pointer;
                }
                .grv-spin {
                    animation: grv-spin 0.9s linear infinite;
                }
                .grv-modal-overlay {
    position: fixed;
    inset: 0;
    background: rgba(0, 0, 0, 0.45);
    display: flex;
    align-items: center;
    justify-content: center;
    z-index: 1000;
    padding: 16px;
}
.grv-modal-card {
    background: #fff;
    border-radius: 14px;
    width: 100%;
    max-width: 360px;
    padding: 28px 24px 22px;
    text-align: center;
    position: relative;
    box-shadow: 0 8px 30px rgba(0, 0, 0, 0.25);
}
.grv-modal-close-x {
    position: absolute;
    top: 10px;
    right: 10px;
    background: #323C43;
    color: #fff;
    border: none;
    border-radius: 50%;
    width: 26px;
    height: 26px;
    font-size: 16px;
    line-height: 1;
    cursor: pointer;
    display: flex;
    align-items: center;
    justify-content: center;
}
.grv-modal-icon {
    font-size: 40px;
    color: var(--cpa-yellow-ink, #8A6D00);
    margin-bottom: 6px;
}
.grv-modal-title {
    font-size: 20px;
    font-weight: 700;
    color: #222;
    margin: 6px 0 10px;
}
.grv-modal-text {
    font-size: 14px;
    color: #555;
    line-height: 1.5;
    margin: 0 0 22px;
}
.grv-modal-actions {
    display: flex;
    gap: 12px;
    justify-content: center;
}
.grv-modal-yes {
    background: #247A33;
    color: #fff;
    border: none;
    border-radius: 8px;
    padding: 10px 18px;
    font-size: 14px;
    font-weight: 600;
    cursor: pointer;
    flex: 1;
}
.grv-modal-no {
    background: #d32f2f;
    color: #fff;
    border: none;
    border-radius: 8px;
    padding: 10px 18px;
    font-size: 14px;
    font-weight: 600;
    cursor: pointer;
    flex: 1;
}    
                @keyframes grv-spin {
                    to { transform: rotate(360deg); }
                }

                @media (max-width: 1024px) {
                    .grv-content {
                        padding: 16px 32px 10px;
                    }
                }

                @media (max-width: 768px) {
                    .grv-content {
                        padding: 14px 20px 10px;
                    }
                    .grv-header-bar h2 {
                        font-size: 18px;
                    }
                    .grv-client-name {
                        font-size: 19px;
                    }
                    .grv-assessment-date {
                        font-size: 18px;
                    }
                    .grv-row-2col {
                        grid-template-columns: 1fr;
                        gap: 12px;
                    }
                    .grv-row-2col.grv-status-date-row {
                        grid-template-columns: 1fr 1fr;
                    }
                    .grv-section-title {
                        font-size: 20px;
                    }
                    .grv-principles-title {
                        font-size: 17px;
                    }
                    .grv-principle-text {
                        font-size: 14px;
                    }
                    .grv-principle-label {
                        font-size: 15px;
                    }
                    .grv-field-inline {
                        flex-direction: column;
                        gap: 8px;
                    }
                    .grv-field-inline label {
                        flex-basis: auto;
                        max-width: 100%;
                    }
                    .grv-outcome-2col {
                        grid-template-columns: 1fr;
                        gap: 12px;
                    }
                    .grv-footer {
                        flex-wrap: wrap;
                    }
                }

                @media (max-width: 480px) {
                    .grv-header-bar {
                        padding: 10px 12px;
                        min-height: 44px;
                    }
                    .grv-header-bar h2 {
                        font-size: 16px;
                    }
                    .grv-back-btn,
                    .grv-back-btn2 {
                        left: 8px;
                        padding: 5px 8px;
                        font-size: 12px;
                    }
                    .grv-content {
                        padding: 12px 12px 8px;
                    }
                    .grv-client-name {
                        font-size: 17px;
                    }
                    .grv-assessment-date {
                        font-size: 15px;
                        margin-bottom: 12px;
                    }
                    .grv-goal-block {
                        padding-bottom: 18px;
                        margin-bottom: 18px;
                    }
                    .grv-field label,
                    .grv-label {
                        font-size: 14px;
                    }
                    .grv-box,
                    .grv-select {
                        font-size: 13px;
                        padding: 8px 10px;
                    }
                    .grv-subheading {
                        font-size: 13px;
                    }
                    .grv-status-note {
                        font-size: 12px;
                        padding: 10px 12px;
                    }
                    .grv-sub-title {
                        font-size: 13px;
                    }
                    .grv-pill {
                        font-size: 12px;
                        padding: 5px 10px;
                    }
                    .grv-intervention-row {
                        flex-wrap: wrap;
                    }
                    .grv-counter-field {
                        max-width: 100%;
                        flex-wrap: wrap;
                        justify-content: space-between;
                    }
                    .grv-counter-field label {
                        font-size: 12px;
                        flex: 1 1 auto;
                        min-width: 0;
                    }
                    .grv-counter {
                        flex: 0 0 auto;
                    }
                    .grv-section-title {
                        font-size: 18px;
                        margin: 18px 0 6px;
                    }
                    .grv-principles-title {
                        font-size: 15px;
                    }
                    .grv-principle-text {
                        font-size: 13px;
                    }
                    .grv-principle-label {
                        font-size: 14px;
                    }
                    .grv-reflect-instructions {
                        font-size: 13px;
                    }
                    .grv-field-inline label {
                        font-size: 14px;
                    }
                    .grv-field-inline textarea,
                    .grv-field textarea,
                    .grv-outcome-box textarea {
                        font-size: 13px;
                        padding: 10px 12px;
                    }
                    .grv-outcome-box {
                        padding: 12px;
                    }
                    .grv-footer {
                        flex-direction: column-reverse;
                        gap: 10px;
                    }
                    .grv-footer button {
                        width: 100%;
                        justify-content: center;
                    }
                }

                @media (max-width: 360px) {
                    .grv-header-bar h2 {
                        font-size: 14px;
                    }
                    .grv-back-btn,
                    .grv-back-btn2 {
                        font-size: 11px;
                        padding: 4px 7px;
                    }
                    .grv-content {
                        padding: 10px 10px 6px;
                    }
                    .grv-counter-input {
                        width: 50px;
                    }
                }

                /* ---- Goal Review summary card ---- */
                .summ-container11 {
                    width: 100%;
                    margin-top: 0px;
                    color: #333;
                    padding: 0 0 15px 0;
                }
                .sum-cont11 {
                    flex: 1;
                    padding: 0px 20px 10px 0px;
                    width: 100%;
                    
                }
                .summ-client-row {
                    text-align: center;
                    font-size: 18px;
                    font-weight: 700;
                    color: #247a33;
                    margin-bottom: 14px;
                }
                .summ-meta-row {
                    display: flex;
                    flex-wrap: wrap;
                    gap: 16px;
                    justify-content: flex-start;
                    align-items: center;
                    margin-bottom: 18px;
                    text-align: left;
                }
                .summ-meta-label {
                    font-size: 14px;
                    font-weight: 600;
                    color: #247a33;
                }
                .summ-meta-value {
                    font-size: 14px;
                    font-weight: 500;
                    color: var(--cpa-primary, #247A3D);
                }
                .summ-meta-pill {
                    display: inline-flex;
                    align-items: center;
                    background: #ffffff;
                    border: 1px solid #dcdcdc;
                    border-radius: 50px;
                    padding: 5px 12px;
                }
                .summ-meta-plain {
                    border: none;
                    background: transparent;
                    border-radius: 0;
                    padding: 0;
                }
                .summ-meta-green .summ-meta-label {
                    color: var(--cpa-primary, #247A3D);
                }
                .summ-section-title {
                    font-size: 18px;
                    font-weight: 700;
                    color: #323C43;
                    margin: 6px 0 14px 0;
                    word-wrap: break-word;
                    overflow-wrap: break-word;
                }
                .summ-findings-block {
                    display: flex;
                    flex-wrap: wrap;
                    gap: 12px;
                    margin-bottom: 10px;
                }
                .summ-finding-row {
                    display: flex;
                    align-items: center;
                    gap: 10px;
                    margin-bottom: 12px;
                    padding: 8px 12px;
                    background: #ffffff;
                    border: 1px solid #dcdcdc;
                    border-radius: 4px;
                    word-wrap: break-word;
                    overflow-wrap: break-word;
                }
                .summ-finding-label {
                    color: #323c43;
                    font-weight: 700;
                    font-size: 15px;
                    white-space: pre-wrap;
                }
                .summ-value-pill {
                    display: inline-block;
                    background: #ffffff;
                    border: 1px solid #dcdcdc;
                    border-radius: 4px;
                    padding: 4px 10px;
                    font-size: 14px;
                    color: #323c43;
                    font-weight: 500;
                }
                .summ-finding-row .summ-value-pill {
                    border: none;
                    background: transparent;
                    border-radius: 0;
                    padding: 0;
                }
                .summ-info-header {
                    font-size: 17px;
                    font-weight: 700;
                    color: #323c43;
                    margin: 20px 0 6px 0;
                    line-height: 1.4;
                }
                .summ-info-text {
                    font-size: 15px;
                    color: #323c43;
                    margin: 0 0 18px 0;
                }
                .icon-circle1 {
                    width: 32px;
                    height: 32px;
                    min-width: 32px;
                    min-height: 32px;
                    background: #247A33;
                    color: white;
                    border-radius: 50%;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    flex-shrink: 0;
                    font-size: 12px;
                }
                @media (max-width: 768px) {
                    .sum-cont1 {
                        padding: 15px 14px 10px 14px;
                    }
                    .summ-client-row {
                        font-size: 20px;
                    }
                    .summ-meta-row {
                        gap: 10px;
                    }
                    .summ-section-title {
                        font-size: 16px;
                    }
                    .summ-finding-label {
                        font-size: 14px;
                    }
                    .summ-info-header {
                        font-size: 15px;
                    }
                    .summ-info-text {
                        font-size: 14px;
                    }
                }
            `}</style>
        </>
    );
}
